import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useScrollMemory } from './scrollMemory'
import {
  Mail, FileText, Paperclip, Layers, Menu, ChevronsRight,
  Bolt, Zap, AlertTriangle, ArrowDown, ArrowUp, ChevronRight, ChevronDown,
  Link2, Image as ImageIcon, Video, File as FileIcon, Sheet, FilePlus, Loader2,
  MessagesSquare, MoreHorizontal,
} from 'lucide-react'
import {
  shortId, getMatterCadence, getCadenceBands, setCadenceBands, matters, manualEdit,
  type Artifact, type Matter, type CadenceBand, type ChangeCarry,
} from '../api'
import { FolderPickerModal } from './FolderSidebar'
import { traceDestination } from '../tracking/searchTrace'
import EditableText from './EditableText'
import DriveMaterialModal from './DriveMaterialModal'
import { MultiIssueBlock } from './MultiIssueBlock'
import { useFlashOnChange } from './useFlashOnChange'
import { useProductName } from '../productName'
import { isPlayheadTenant } from '../playhead'
import { Glyph, glyphLabel } from '../glyphs'
import { useAuth } from '../auth'
import { useCounterWindow, windowToHours } from '../counterWindow'

type NarrativeGroup = NonNullable<Matter['narrative_groups']>[number]

/**
 * Matter timeline pane — the middle column in the workspace when a
 * matter is selected. Mirrors the React prototype:
 *   v2_design_docs/Working Thinking Claude Design prototype/src/workspace.jsx
 *   (TimelinePane + StageGroupedTimeline + SpineTimeline + FeedTimeline)
 *
 * Three view modes:
 *   - Spine — vertical line, dot per artifact, chronological newest-first
 *   - Stages — grouped by per-matter-type stage templates, time-bucketed
 *   - Feed — flat list with chips inline (urgency, etc.)
 *
 * The user has a default style (stored on me_timeline_style); each
 * matter can lock its own override (stored as a map under
 * me_matter_style). Lock icon next to the segmented control.
 */

type TimelineStyle = 'spine' | 'activity' | 'feed' | 'documents'

const DEFAULT_STYLE_KEY = 'me_timeline_style'
const PER_MATTER_STYLE_KEY = 'me_matter_style'

function loadDefaultStyle(): TimelineStyle {
  try {
    const v = localStorage.getItem(DEFAULT_STYLE_KEY)
    // Migrate legacy 'stages' value to 'activity' so users with it pinned
    // still land on the same tab post-rename.
    if (v === 'stages') return 'activity'
    if (v === 'spine' || v === 'feed' || v === 'activity') return v
  } catch { /* ignore */ }
  return 'feed'
}

function loadPerMatterStyles(): Record<number, TimelineStyle> {
  try {
    const raw: Record<number, string> = JSON.parse(
      localStorage.getItem(PER_MATTER_STYLE_KEY) || '{}'
    )
    // Migrate legacy 'stages' values to 'activity' (post-rename).
    const out: Record<number, TimelineStyle> = {}
    for (const [k, v] of Object.entries(raw)) {
      const mapped = v === 'stages' ? 'activity' : v
      if (mapped === 'spine' || mapped === 'feed' || mapped === 'activity') {
        out[Number(k)] = mapped
      }
    }
    return out
  } catch {
    return {}
  }
}

// ─── Tray (Activity) collapse/expand state ────────────────────────────────
// Which activity categories the user has collapsed in the Tray view, kept
// PER MATTER so a curated set of open/closed sections survives navigation
// and refresh. Default is expanded (a label is only present here once the
// user explicitly collapses it), so this is additive to prior behavior.
// Category labels are AI-generated and can change when Categorize re-runs;
// orphaned labels are harmless (a label no longer present just goes unused,
// and any new label defaults to expanded).
const TRAY_COLLAPSED_KEY = 'me_tray_collapsed'

function loadTrayCollapsed(matterId: number): Set<string> {
  try {
    const raw: Record<number, string[]> = JSON.parse(
      localStorage.getItem(TRAY_COLLAPSED_KEY) || '{}'
    )
    return new Set(raw[matterId] || [])
  } catch {
    return new Set()
  }
}

function saveTrayCollapsed(matterId: number, collapsed: Set<string>) {
  try {
    const raw: Record<number, string[]> = JSON.parse(
      localStorage.getItem(TRAY_COLLAPSED_KEY) || '{}'
    )
    if (collapsed.size === 0) delete raw[matterId]
    else raw[matterId] = [...collapsed]
    localStorage.setItem(TRAY_COLLAPSED_KEY, JSON.stringify(raw))
  } catch { /* ignore */ }
}

// Stage templates removed: the old Stages view (static template per
// matter_type) was replaced with the Activity view in this file, which
// reads dividers from matter.activity_index (output of "Categorize Issues
// by Legal Activity Type"). Backend STAGE_TEMPLATES still drives
// ArtifactMatter.stage labels elsewhere.

// ─── Top-level pane ──────────────────────────────────────────────────────

type Props = {
  matter: Matter
  artifacts: Artifact[]
  selectedArtifactId: number | null
  /** Pass `null` to clear the selection (closes the right detail pane). */
  onSelectArtifact: (id: number | null) => void
  /** The artifact currently being viewed in pane-3 "markup" mode (the
   *  attachment whose green badge was clicked). Used so the badge can
   *  render an "open" affordance. Null when no markup is active.
   *  Also drives the "click attachment card again to exit markup"
   *  behavior — Workspace's onSelectArtifact compares the click target
   *  against this id. */
  markupArtifactId?: number | null
  /** Called when the green "markup" badge on an attachment card is
   *  clicked. Workspace makes that artifact selected AND enables
   *  markup mode atomically (single URL update). */
  onOpenMarkup?: (artifactId: number) => void
  /** Open the "changes carried" modal for an executed attachment (Ken 2026-11-02). */
  onOpenChangeCarry?: (data: ChangeCarry, filename: string) => void
  /** Slot for the bulk-action toolbar from Workspace, rendered above
   *  the timeline area but below the matter header. */
  toolbar?: React.ReactNode
  /** sessionStorage key for restoring this pane's scroll position.
   *  Workspace passes `me.scroll.artifacts.<matterId>` so each matter
   *  remembers where the user was. */
  scrollKey?: string
  /** True while react-query is fetching a fresh artifacts list. With
   *  keepPreviousData enabled upstream, we keep the previous matter's
   *  list visible (rather than blanking) but dim it + show a loading
   *  hint so the user knows it isn't fresh yet. */
  isFetching?: boolean
}

// True when the open matter is a CIC demo (studio-only "proposed" re-do). Lets
// deep card components flag it / suppress dev placeholders without prop-threading
// through every timeline view. Ken 2026-10-01.
const MatterDemoContext = createContext(false)

// KEN "Least noisy" (Ken 2026-10-23, #3): when true, the chronology cards drop
// the subject line (keeping the AI summary + the id/sender/date meta line). Set
// only on the playhead (KEN/DEMO) branch; default false leaves MOVU and every
// non-playhead surface (incl. the Control-B preview) byte-for-byte unchanged.
// Carried by context so the deep card doesn't need a new threaded prop.
const HideSubjectContext = createContext<{ playhead: boolean; hide: boolean }>({ playhead: false, hide: false })

export default function MatterTimelinePane({
  matter, artifacts, selectedArtifactId, onSelectArtifact,
  markupArtifactId = null, onOpenMarkup, onOpenChangeCarry,
  toolbar, scrollKey, isFetching,
}: Props) {
  const scrollRef = useRef<HTMLElement>(null)
  // Per-matter scroll restore on every key change — restoring "your place
  // in matter X" feels expected when bouncing between matters or coming
  // back later. (Workspace controls the global matters list separately
  // via the pending-flag mechanism.)
  useScrollMemory(scrollRef, scrollKey ?? `me.scroll.artifacts.${matter.id}`, 'always')

  // ── DEMO "as-of" playhead ──────────────────────────────────────────────────
  // DEMO tenant ONLY (Ken 2026-08-24). For the demo we repurpose the sidebar
  // newness dial as a historical PLAYHEAD instead of a lookback window: its
  // position names a past moment `asof = now − dialPosition`, and we render the
  // matter as it existed then — hide every artifact dated AFTER asof. Slide
  // toward the present and the chronology/phases accrete from the story's origin
  // forward. This is a pure front-end overlay on the already-fetched artifact
  // list; it NEVER runs for a real tenant, so v2's live "last N" window semantics
  // are completely untouched. 'all'/near-now dial → no cutoff → full present-day
  // story. Phase groupings resolve against the filtered list downstream, so they
  // prune to whatever artifacts survive the cutoff automatically.
  const auth = useAuth()
  const isDemoPlayhead = auth.status === 'authenticated'
    && isPlayheadTenant(auth.user.client?.code)
  // Subscribe to the dial ONLY for the demo — real tenants pass enabled=false so
  // this pane doesn't re-render on every dial drag (its timeline children aren't
  // memoized). v2 behaviour is byte-for-byte unchanged.
  const demoWindow = useCounterWindow(isDemoPlayhead)
  const asofCutoffMs = useMemo<number | null>(() => {
    if (!isDemoPlayhead) return null
    const h = windowToHours(demoWindow)   // hours-back, or null for all-time
    if (h == null) return null            // all-time / "now" → present, no cutoff
    return Date.now() - h * 3.6e6
  }, [isDemoPlayhead, demoWindow])
  const paneArtifacts = useMemo(() => {
    if (asofCutoffMs == null) return artifacts
    return artifacts.filter((a) => {
      if (!a.source_date) return true     // undated → never hide (safety)
      return new Date(a.source_date).getTime() <= asofCutoffMs
    })
  }, [artifacts, asofCutoffMs])
  const [defaultStyle, setDefaultStyle] = useState<TimelineStyle>(loadDefaultStyle)
  const [perMatterStyle, setPerMatterStyle] = useState<Record<number, TimelineStyle>>(loadPerMatterStyles)
  // `showDetails` = the FULL record (subject line + sender/date/id/issue meta).
  // The UI button reads "cleanest view" and is the INVERSE of this flag: checked
  // = reduced = showDetails OFF (Ken 2026-08-18). Reduced view shows only a slim
  // "shortId · date" anchor line + the AI summary; full view adds the subject +
  // meta (incl. issue N of M). Default: showDetails OFF (cleanest view ON) —
  // browsing a matter is usually about scanning AI summaries. Persisted to
  // localStorage (the key stores showDetails; the button just displays !it).
  const [showDetails, setShowDetailsState] = useState<boolean>(() => {
    try { return localStorage.getItem('me_artifact_show_subj') === '1' }
    catch { return false }
  })
  const setShowDetails = (v: boolean) => {
    setShowDetailsState(v)
    try { localStorage.setItem('me_artifact_show_subj', v ? '1' : '0') } catch { /* ignore */ }
  }
  // KEN "Least noisy" toggle (Ken 2026-10-23, #3). Playhead only — it replaces
  // the old "Clean view" (showDetails) toggle for KEN. When ON the chronology
  // cards hide the subject line but keep the stem summary + id/sender/date meta.
  // For the playhead, showDetails is forced ON below so that meta line always
  // renders; this flag then subtracts just the subject. Default ON (least noisy),
  // matching KEN's prior cleanest-by-default preference. Own localStorage key so
  // it doesn't disturb MOVU's showDetails persistence.
  const [hideSubject, setHideSubjectState] = useState<boolean>(() => {
    try { return localStorage.getItem('me_artifact_hide_subj') !== '0' }
    catch { return true }
  })
  const setHideSubject = (v: boolean) => {
    setHideSubjectState(v)
    try { localStorage.setItem('me_artifact_hide_subj', v ? '1' : '0') } catch { /* ignore */ }
  }
  const [sortDir, setSortDir] = useState<'desc' | 'asc'>(() => {
    try {
      const v = localStorage.getItem('me_timeline_sort_dir')
      return v === 'asc' ? 'asc' : 'desc'
    } catch {
      return 'desc'
    }
  })
  const toggleSortDir = () => {
    const next = sortDir === 'desc' ? 'asc' : 'desc'
    setSortDir(next)
    try { localStorage.setItem('me_timeline_sort_dir', next) } catch { /* ignore */ }
  }

  const lockedStyle = perMatterStyle[matter.id]
  const activeStyle: TimelineStyle = lockedStyle || defaultStyle
  const isLocked = !!lockedStyle
  // Tray (Activity) view only exists for matters with a populated
  // activity_index (litigation/contract, per the categorize-activity gate).
  // Hide the Tray button otherwise, and fall the rendered view back to Phase
  // if a matter was pinned to Tray but no longer offers one.
  // Tray also exists when there are content-free followups (A′) to house, even
  // if the matter never got an LLM activity_index (Ken 2026-08-23).
  // `matter` here is the SLIM list object (no phase_groupings/activity_index/
  // email_issues — those dominate the list payload and are omitted; Ken
  // 2026-09-25). It carries `has_activity_index` so the Tray toggle still
  // shows without the full array.
  const hasTray = !!matter.has_activity_index || paneArtifacts.some((a) => !!a.followup_kind)
  // Docs view shows up when the matter has standalone documents OR any artifact
  // carries doc-type tags (the KEN demo groups emails + attachments by Doc Type).
  const hasDocuments = useMemo(
    () => paneArtifacts.some((a) => a.artifact_type === 'document' || (a.doc_types?.length ?? 0) > 0),
    [paneArtifacts])
  const effectiveStyle: TimelineStyle =
    activeStyle === 'activity' && !hasTray ? 'spine'
      : activeStyle === 'documents' && !hasDocuments ? 'spine'
        : activeStyle

  // Lazy-load the heavy Phases/Tray bundle (phase_groupings + activity_index +
  // their email_issues resolver) ONLY when one of those views is active — most
  // sessions stay on Chronology and never pay it. Fetches the full matter via
  // GET /matters/{id}/; merged over the slim `matter` so SpineTimeline /
  // ActivityTimeline read the heavy fields off the prop unchanged.
  const needsBundle = effectiveStyle === 'spine' || effectiveStyle === 'activity'
  const bundleQ = useQuery({
    queryKey: ['matter', matter.id, 'bundle'],
    queryFn: () => matters.detail(matter.id),
    enabled: needsBundle,
    staleTime: 30_000,
  })
  const matterForViews = useMemo(
    () => (bundleQ.data ? { ...matter, ...bundleQ.data } : matter),
    [matter, bundleQ.data],
  )

  const setStyle = (style: TimelineStyle, lock: boolean) => {
    if (lock) {
      const next = { ...perMatterStyle, [matter.id]: style }
      setPerMatterStyle(next)
      try { localStorage.setItem(PER_MATTER_STYLE_KEY, JSON.stringify(next)) } catch { /* ignore */ }
    } else {
      const next = { ...perMatterStyle }
      delete next[matter.id]
      setPerMatterStyle(next)
      try { localStorage.setItem(PER_MATTER_STYLE_KEY, JSON.stringify(next)) } catch { /* ignore */ }
      // Also bump the user default if the click was a style change
      // while not locked (mirrors the prototype's behavior).
      if (style !== defaultStyle && !isLocked) {
        setDefaultStyle(style)
        try { localStorage.setItem(DEFAULT_STYLE_KEY, style) } catch { /* ignore */ }
      }
    }
  }

  const sorted = useMemo(
    () => [...paneArtifacts].sort((a, b) => {
      const ad = a.source_date || ''
      const bd = b.source_date || ''
      return sortDir === 'desc' ? bd.localeCompare(ad) : ad.localeCompare(bd)
    }),
    [paneArtifacts, sortDir],
  )
  // Documents live only in the Docs view — excluded from the chronological
  // views (Chron / Phase / Tray) since a standalone doc's date isn't a
  // meaningful timeline event and just clutters/confuses the order
  // (Ken 2026-08-18).
  const chronologyArtifacts = useMemo(
    () => sorted.filter((a) => a.artifact_type !== 'document'),
    [sorted],
  )
  // Content-free followups (A′) are kept OUT of the Chronology (Feed) and Phases
  // (Spine) — they live only in the Tray (ActivityTimeline handles that). So the
  // two chronological views get the followup-free list; the Tray gets the full
  // one. Ken 2026-08-23.
  const timelineArtifacts = useMemo(
    () => chronologyArtifacts.filter((a) => !a.followup_kind),
    [chronologyArtifacts],
  )

  // KEN (Ken 2026-10-23, #3): the playhead always renders the full record so the
  // "Least noisy" toggle has an id/sender/date meta line to keep when it drops the
  // subject. MOVU keeps its own showDetails state untouched. The subject itself is
  // suppressed via HideSubjectContext below (false off the playhead).
  const effectiveDetails = isDemoPlayhead ? true : showDetails

  // Per Ken 2026-05-25 (blue box markup): the top region — matter
  // header (name + summary + bullets + folder controls) AND the
  // view-mode/sort controls — stays fixed; only the chronology list
  // below scrolls. So the outer section is overflow-hidden; the top
  // stays at its natural height; the bottom <div> is the scroll
  // container.
  return (
    <MatterDemoContext.Provider value={!!matter.demo}>
    <HideSubjectContext.Provider value={{ playhead: isDemoPlayhead, hide: isDemoPlayhead && hideSubject }}>
    <section className="me-scroll flex h-full flex-col overflow-hidden bg-[color:var(--me-surface-2)]">
      {/* Fixed (always-visible) top region — header + toolbar + controls. */}
      <div className="shrink-0">
        <MatterHeader matter={matter} />

        {/* Bulk-action toolbar (passed in from Workspace). Only rendered
            if Workspace actually passes one — currently it doesn't for
            the matter view, since bulk-run lives in Studio. */}
        {toolbar && <div className="px-3 pt-2">{toolbar}</div>}

        {/* View / sort controls. Inherits the pane's surface background. KEN
            (Ken 2026-10-23 #2): no rule above — spacing alone separates it from
            the matter brief; #3/#4: symmetric 14.76px gaps above (pt 4.92 + header
            pb 9.84) and below (pb 4.92 + list pt 9.84). */}
        <div className={`flex gap-1 px-3 ${isDemoPlayhead ? 'items-baseline py-[4.92px]' : 'items-center border-t me-border py-1.5'}`}>
          <ViewModeControls
            style={effectiveStyle}
            isLocked={isLocked}
            onStyleChange={setStyle}
            hasTray={hasTray}
            hasDocuments={hasDocuments}
          />
          {isDemoPlayhead ? (
            // pane2.toolbar.subject_toggle (Ken 2026-10-25): SHOW/HIDE SUBJECTS,
            // right-aligned on the switcher's baseline; same hideSubject logic — the
            // label names the action (shown → HIDE SUBJECTS; hidden → SHOW SUBJECTS).
            <button
              type="button"
              onClick={() => setHideSubject(!hideSubject)}
              aria-pressed={hideSubject}
              // Exactly "Last 8 hours" (Ken 2026-10-25): chrome/value (sans), sentence
              // case, and me-pane1-size cancels the pane-2 reading-zoom so it renders
              // at the pane-1 window value's size, not the larger pane-2 scale.
              className="me-chrome-value ml-auto cursor-pointer select-none"
            >{hideSubject ? 'show Subject Lines' : 'hide Subject Lines'}</button>
          ) : (
            <>
              <label
                className="me-brand ml-2 inline-flex cursor-pointer items-center gap-1.5 text-[11px] me-text-mute select-none"
              >
                <input
                  type="checkbox"
                  checked={!showDetails}
                  onChange={(e) => setShowDetails(!e.target.checked)}
                  className="me-check-plain"
                />
                cleanest view
              </label>
              <span className="ml-auto" />
              {/* Sort arrow: on the switcher row for real tenants; for KEN it moves
                  below the divider to the head of the list (SPEC-0001). */}
              <SortToggle sortDir={sortDir} onToggle={toggleSortDir} />
            </>
          )}
        </div>
      </div>

      {/* Scrollable chronology. While the parent's artifacts query is
          loading (matter switch or first load with no cached data), the
          list is empty and a centered spinner takes over. Background
          refetches on already-loaded data don't fire this — only true
          empty states. */}
      <div
        ref={scrollRef as React.RefObject<HTMLDivElement>}
        className={`me-scroll relative flex-1 overflow-auto px-3 ${isDemoPlayhead ? 'pt-[9.84px] pb-2' : 'py-2'}`}
        aria-busy={isFetching ? 'true' : undefined}
      >
        {/* Sort arrow relocated here for KEN (SPEC-0001): below the divider, at the
            head of the list, right-aligned, muted — it's a list control, not a view. */}
        {isDemoPlayhead && sorted.length > 0 && (
          <div className="mb-1 flex justify-end">
            <SortToggle sortDir={sortDir} onToggle={toggleSortDir} muted />
          </div>
        )}
        {isFetching && (
          <div
            className="pointer-events-none absolute inset-0 z-10 flex items-start justify-center pt-16"
            aria-label="Loading"
            role="status"
          >
            <div className="size-8 rounded-full border-[3px] border-[color:var(--me-text-mute)] border-t-transparent animate-spin" />
          </div>
        )}
        {sorted.length === 0 && !isFetching && (
          <div className="rounded-sm border border-dashed me-border p-3 text-[12px] me-text-mute">
            No artifacts on this matter yet.
          </div>
        )}
        {sorted.length > 0 && chronologyArtifacts.length === 0 && effectiveStyle !== 'documents' && !isFetching && (
          <div className="rounded-sm border border-dashed me-border p-3 text-[12px] me-text-mute">
            This matter has only documents — open the <span className="me-mono">Docs</span> view.
          </div>
        )}
        {/* Phases/Tray need the lazily-fetched bundle — spinner while it loads. */}
        {needsBundle && bundleQ.isLoading && (
          <div className="flex items-center gap-2 p-3 text-[12px] me-text-mute">
            <Loader2 size={13} className="animate-spin" /> Loading {effectiveStyle === 'spine' ? 'phases' : 'tray'}…
          </div>
        )}
        {needsBundle && bundleQ.isError && (
          <div className="rounded-sm border border-dashed me-border p-3 text-[12px] me-text-mute">
            Couldn’t load this view — switch views and back to retry.
          </div>
        )}
        {timelineArtifacts.length > 0 && effectiveStyle === 'spine' && bundleQ.data && (
          <SpineTimeline
            artifacts={timelineArtifacts}
            matter={matterForViews}
            selectedId={selectedArtifactId}
            onSelect={onSelectArtifact}
            showDetails={effectiveDetails}
            sortDir={sortDir}
            markupArtifactId={markupArtifactId}
            onOpenMarkup={onOpenMarkup} onOpenChangeCarry={onOpenChangeCarry}
          />
        )}
        {chronologyArtifacts.length > 0 && effectiveStyle === 'activity' && bundleQ.data && (
          <ActivityTimeline
            artifacts={chronologyArtifacts}
            matter={matterForViews}
            selectedId={selectedArtifactId}
            onSelect={onSelectArtifact}
            sortDir={sortDir}
            showDetails={effectiveDetails}
            markupArtifactId={markupArtifactId}
            onOpenMarkup={onOpenMarkup} onOpenChangeCarry={onOpenChangeCarry}
          />
        )}
        {timelineArtifacts.length > 0 && effectiveStyle === 'feed' && (
          <FeedTimeline
            artifacts={timelineArtifacts}
            matterId={matter.id}
            selectedId={selectedArtifactId}
            onSelect={onSelectArtifact}
            showDetails={effectiveDetails}
            markupArtifactId={markupArtifactId}
            onOpenMarkup={onOpenMarkup} onOpenChangeCarry={onOpenChangeCarry}
          />
        )}
        {sorted.length > 0 && effectiveStyle === 'documents' && (
          <DocumentsTimeline
            artifacts={sorted}
            matterId={matter.id}
            selectedId={selectedArtifactId}
            onSelect={onSelectArtifact}
            showDetails={effectiveDetails}
            markupArtifactId={markupArtifactId}
            onOpenMarkup={onOpenMarkup} onOpenChangeCarry={onOpenChangeCarry}
          />
        )}
      </div>
    </section>
    </HideSubjectContext.Provider>
    </MatterDemoContext.Provider>
  )
}

// ─── Cadence dot (matter status color, Heather 2026-08-19) ────────────────
// The one honest, non-perishable status signal: days since the newest email /
// doc landed, expressed ONLY as a color (no number — the screen has enough).
// Hover reveals the count; click opens the "time borders" editor.

function cadenceColor(days: number | null, bands: CadenceBand[]): string {
  if (days === null || !bands.length) return ''
  for (const b of bands) {
    if (b.max_days === null || days <= b.max_days) return b.color
  }
  return bands[bands.length - 1].color
}

// The DISPLAYED chip uses a lighter, translucent version of the band color so
// it reads as ambient status, not an alarm — the editor keeps the true vivid
// colors (Ken 2026-08-19).
function withAlpha(hex: string, a: number): string {
  const m = /^#?([0-9a-f]{6})$/i.exec((hex || '').trim())
  if (!m) return hex
  const n = parseInt(m[1], 16)
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${a})`
}

function CadenceBandsEditor({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient()
  const bandsQ = useQuery({ queryKey: ['cadence-bands'], queryFn: getCadenceBands, staleTime: 60_000 })
  const [rows, setRows] = useState<CadenceBand[]>([])
  useEffect(() => { if (bandsQ.data) setRows(bandsQ.data) }, [bandsQ.data])
  const save = useMutation({
    mutationFn: () => setCadenceBands(rows),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['cadence-bands'] }); onClose() },
  })
  const setRow = (i: number, patch: Partial<CadenceBand>) =>
    setRows((rs) => rs.map((r, j) => (j === i ? { ...r, ...patch } : r)))
  const addRow = () => setRows((rs) => {
    const catchAll = rs.findIndex((r) => r.max_days === null)
    const next = [...rs]
    next.splice(catchAll < 0 ? rs.length : catchAll, 0, { max_days: 30, color: '#888888' })
    return next
  })
  const removeRow = (i: number) => setRows((rs) => rs.filter((_, j) => j !== i))
  return (
    <>
      <div className="fixed inset-0 z-30" onClick={onClose} />
      <div
        className="absolute left-0 top-6 z-40 w-64 rounded border me-border p-3 shadow-lg"
        style={{ background: 'var(--me-bg, #fff)' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-2 text-[12px] font-medium me-text">Days since last activity</div>
        <div className="flex flex-col gap-1.5">
          {rows.map((r, i) => (
            <div key={i} className="flex items-center gap-2 text-[11px]">
              {/* Fixed-width label column → color pickers align AND sit close to
                  "days" (no flex-1 gap, Ken 2026-08-19). */}
              <div className="flex w-24 items-center gap-1.5">
                <span className="me-text-mute">≤</span>
                {r.max_days === null ? (
                  <span className="italic me-text-mute">older</span>
                ) : (
                  <>
                    <input type="number" min={0}
                           value={r.max_days ? r.max_days : ''}
                           onChange={(e) => {
                             const n = parseInt(e.target.value, 10)
                             setRow(i, { max_days: Number.isNaN(n) ? 0 : Math.max(0, n) })
                           }}
                           className="w-12 rounded border me-border bg-transparent px-1 py-0.5 me-text" />
                    <span className="me-text-mute">days</span>
                  </>
                )}
              </div>
              <input type="color" value={r.color} onChange={(e) => setRow(i, { color: e.target.value })}
                     className="h-5 w-7 shrink-0 cursor-pointer rounded border me-border bg-transparent p-0" title="color" />
              <span className="flex w-4 shrink-0 justify-center">
                {r.max_days !== null && rows.length > 2 && (
                  <button onClick={() => removeRow(i)} className="me-text-mute hover:me-text" title="Remove">×</button>
                )}
              </span>
            </div>
          ))}
        </div>
        <div className="mt-2 flex items-center justify-between">
          <button onClick={addRow} className="text-[11px] me-text-mute hover:me-text">+ band</button>
          <button onClick={() => save.mutate()} disabled={save.isPending}
                  className="me-chrome me-btn me-btn-outline !text-[11px] !px-2 !py-0.5">
            {save.isPending ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </>
  )
}

function CadenceDot({ matter }: { matter: Matter }) {
  const [editing, setEditing] = useState(false)
  // KEN/playhead (Ken 2026-10-22 A): the cadence rectangle de-colours — neutral,
  // no fill, hairline outline — as it becomes the (later-wired) one-line answer
  // handle. Colour-coding stays for MOVU.
  const cdAuth = useAuth()
  const cdPlayhead = cdAuth.status === 'authenticated' && isPlayheadTenant(cdAuth.user.client?.code)
  const cadence = useQuery({ queryKey: ['cadence', matter.id], queryFn: () => getMatterCadence(matter.id) })
  const bandsQ = useQuery({ queryKey: ['cadence-bands'], queryFn: getCadenceBands, staleTime: 60_000 })
  const days = cadence.data?.days_since ?? null
  const color = cadenceColor(days, bandsQ.data || [])
  const label = days === null ? 'no email or doc yet' : `${days} day${days === 1 ? '' : 's'}`
  // KEN (Ken 2026-10-23, #11): de-coloured, it was just an empty outlined box with
  // nothing to show (its one-line-answer hover isn't wired yet). Render nothing
  // rather than an empty container. MOVU keeps the colour-coded cadence dot.
  if (cdPlayhead) return null
  return (
    <span className="group relative inline-flex shrink-0 items-center self-center">
      <button onClick={() => setEditing((v) => !v)} aria-label={label}
              className="inline-flex items-center">
        {/* A label-less rounded RECTANGLE (not a circle) so it never reads as one
            of the round "new" pips — distinct shape, matter-level metadata slot
            next to the M-number (Ken 2026-08-19). */}
        <span className="rounded-[3px]" style={{
          width: 32, height: 10,
          background: cdPlayhead ? 'transparent' : ((days === null || !color) ? 'transparent' : withAlpha(color, 0.3)),
          border: cdPlayhead
            ? '1px solid var(--me-hairline-color, var(--me-borderStrong, #b8b2a8))'
            : ((days === null || !color) ? '1.5px solid var(--me-borderStrong, #b8b2a8)' : 'none'),
          boxShadow: cdPlayhead ? 'none' : ((days !== null && color) ? '0 0 0 1px rgba(0,0,0,0.06)' : 'none'),
        }} />
      </button>
      {/* Instant hover reveal — just the number ("21 days"), no native-title
          delay (Ken 2026-08-19). Hidden while the editor is open. */}
      {!editing && (
        <span
          className="pointer-events-none absolute left-1/2 top-full z-30 mt-1 -translate-x-1/2 whitespace-nowrap rounded border me-border px-1.5 py-0.5 text-[10px] me-text opacity-0 transition-opacity duration-75 group-hover:opacity-100"
          style={{ background: 'var(--me-bg, #fff)' }}
        >
          {label}
        </span>
      )}
      {editing && <CadenceBandsEditor onClose={() => setEditing(false)} />}
    </span>
  )
}

// ─── Header ──────────────────────────────────────────────────────────────

function MatterHeader({ matter }: { matter: Matter }) {
  const code = matter.composite_id || `M-${matter.client_seq.toString().padStart(3, '0')}`
  const [driveOpen, setDriveOpen] = useState(false)
  const mhAuth = useAuth()
  const mhPlayhead = mhAuth.status === 'authenticated' && isPlayheadTenant(mhAuth.user.client?.code)
  const inv: readonly (string | number | object)[][] = [
    ['matters', { limit: 500 }],
    ['matter', matter.id],
  ]
  const nameFlash = useFlashOnChange(matter.name, matter.id)
  const urgencyFlash = useFlashOnChange(matter.urgency, matter.id)
  // SPEC-0005 (KEN): the pane-2 header is managed by a three-dots menu (Rename… /
  // Add material… / Move to… / Archive); Rename turns the title into an in-place
  // field in its own type. Hooks declared unconditionally (Rules of Hooks).
  const qc = useQueryClient()
  const [renaming, setRenaming] = useState(false)
  const [draft, setDraft] = useState(matter.name)
  const [manageOpen, setManageOpen] = useState(false)
  const [pickerOpen, setPickerOpen] = useState(false)
  const renameMut = useMutation({
    // Same manual-edit endpoint EditableText uses; no reason (send none).
    mutationFn: () => manualEdit({ target_type: 'matter', target_id: matter.id, field: 'name', value: draft.trim(), reason: undefined }),
    onSuccess: () => { for (const k of inv) qc.invalidateQueries({ queryKey: k }); setRenaming(false) },
  })
  function startRename() { setDraft(matter.name); setRenaming(true) }
  function commitRename() { const v = draft.trim(); if (!v || v === matter.name) { setRenaming(false); return } renameMut.mutate() }
  // Ken 2026-07-08 memo — pane-2 matter-summary sub-area is stripped
  // down to the name + code/type row. paragraph_summary, bullet_summary,
  // the L9 experimental/regenerate panel, the activity index, and the
  // folder chips (icon + name + manage + archive) are all hidden —
  // those controls live in the folders sidebar and their summaries were
  // redundant with the per-artifact cards in the chronology. Matter
  // name font-size bumps to 20px (~20% up from 17px) so pane-2 reads
  // as the parent container relative to pane-3's item-level header.
  // The outer section (see caller) already splits into a fixed top
  // (shrink-0) and a scrollable chronology (overflow-auto), so with
  // the internal scroll region gone the matter name row stays locked
  // in place while the chronology scrolls independently.
  if (mhPlayhead) {
    // SPEC-0005 pane-2 header (KEN). At rest: name + M-### only. Manage (⋯) on
    // hover; Rename… turns the title into an in-place FIND-grammar field.
    const menuItem = 'block w-full rounded px-2 py-1 text-left text-[12px] me-text hover:bg-[color:var(--me-selected)]'
    return (
      <div className="px-4 pt-3 pb-[9.84px]">
        <div className="group/hdr flex items-start gap-2">
          {renaming ? (
            <div className="flex-1 min-w-0">
              <input
                autoFocus
                value={draft}
                maxLength={255}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') { e.preventDefault(); commitRename() }
                  else if (e.key === 'Escape') { e.preventDefault(); setRenaming(false) }
                }}
                className="me-summary me-matter-title text-[24px] font-normal tracking-tight me-text leading-tight w-full bg-transparent outline-none"
                style={{ border: 'none', borderBottom: '1px solid #C9C2B5', padding: 0 }}
              />
              {/* Two plain words — no box, no fill, no glyph, no hint line. */}
              <div className="mt-1 flex items-center" style={{ gap: 18 }}>
                <button type="button" onClick={commitRename} disabled={renameMut.isPending}
                  className="me-chrome text-[13.5px]" style={{ color: '#B24F2F', background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}>Save</button>
                <button type="button" onClick={() => setRenaming(false)}
                  className="me-chrome text-[13.5px]" style={{ color: '#9B9384', background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}>Cancel</button>
              </div>
            </div>
          ) : (
            <>
              <h1 className={`me-summary me-matter-title text-[24px] font-normal tracking-tight me-text leading-tight flex-1 min-w-0 break-words ${nameFlash ? 'me-flash' : ''}`}>{matter.name}</h1>
              {matter.urgency >= 4 && (
                <span className={`me-chip me-chip-urgent flex items-center gap-1 shrink-0 ${urgencyFlash ? 'me-flash' : ''}`}><Bolt size={9} /> urgency {matter.urgency}/5</span>
              )}
              {/* Manage (⋯) — reveals on hover of the title block, right-aligned on
                  the first line. Same three-dots grammar as pane-1 rows (GLY-0003). */}
              <div className="relative shrink-0" style={{ width: 26, height: 26 }} onMouseLeave={() => setManageOpen(false)}>
                <button
                  type="button"
                  onClick={() => setManageOpen((v) => !v)}
                  data-open={manageOpen ? 'true' : undefined}
                  className="inline-flex items-center justify-center rounded me-text-mute hover:me-text opacity-0 transition-opacity duration-[120ms] group-hover/hdr:opacity-100 data-[open=true]:opacity-100"
                  style={{ width: 26, height: 26 }}
                  title="Manage this matter"
                  aria-label="Manage this matter"
                >
                  <Glyph name="manage" size={15}><MoreHorizontal size={15} /></Glyph>
                </button>
                {manageOpen && (
                  <>
                    <div className="fixed inset-0 z-20" onPointerDown={() => setManageOpen(false)} aria-hidden="true" />
                    <div className="absolute right-0 top-full z-30 mt-0.5 w-40 rounded border me-border p-1 shadow-lg" style={{ background: 'var(--me-bg, #fff)' }}>
                      <button type="button" className={menuItem} onClick={() => { setManageOpen(false); startRename() }}>Rename…</button>
                      <button type="button" className={menuItem} onClick={() => { setManageOpen(false); setDriveOpen(true) }}>Add material…</button>
                      <button type="button" className={menuItem} onClick={() => { setManageOpen(false); setPickerOpen(true) }}>Move to…</button>
                      <button type="button" className={`${menuItem} opacity-45 cursor-default`} title="Archive — not yet wired from this pane" onClick={() => setManageOpen(false)}>Archive</button>
                    </div>
                  </>
                )}
              </div>
            </>
          )}
        </div>
        {/* At rest: only the M-### under the name — no pencil, no Add-material
            button; CadenceDot returns null on KEN. */}
        <div className="mt-2 flex flex-wrap items-center gap-2 me-text-mute">
          <span className="me-identifier me-chrome-id tabular-nums">{code}</span>
          <CadenceDot matter={matter} />
        </div>
        <DriveMaterialModal matter={{ id: matter.id, code, name: matter.name }} open={driveOpen} onClose={() => setDriveOpen(false)} />
        {pickerOpen && (
          <FolderPickerModal
            matter={matter}
            currentFolderIds={new Set((matter.folder_assignments || []).map((a) => a.folder))}
            onClose={() => setPickerOpen(false)}
          />
        )}
      </div>
    )
  }

  return (
    <div className={`px-4 ${mhPlayhead ? 'pt-3 pb-[9.84px]' : 'border-b me-border py-3'}`}>
      <div className="flex items-center gap-2">
        <h1
          className={`me-summary me-matter-title text-[24px] font-normal tracking-tight me-text leading-tight flex-1 min-w-0 break-words ${nameFlash ? 'me-flash' : ''}`}
        >
          <EditableText
            value={matter.name}
            target={{ type: 'matter', id: matter.id, field: 'name' }}
            mode="inline"
            maxLength={255}
            invalidateKeys={inv}
          />
        </h1>
        {matter.urgency >= 4 && (
          <span className={`me-chip me-chip-urgent flex items-center gap-1 shrink-0 ${urgencyFlash ? 'me-flash' : ''}`}>
            <Bolt size={9} /> urgency {matter.urgency}/5
          </span>
        )}
      </div>
      {/* Inert text-entry box under the headline hidden per Ken 2026-08-11
          (it had no wired purpose yet). */}
      {/* me-identifier class → hidden when Tweaks → IDENTIFIERS is OFF.
          Kind chip dropped (Emerson 2026-08-07) — matter `kind` is no longer
          shown or user-editable in pane-2. The field still exists and drives
          downstream logic (e.g. the litigation Tray); only its display/edit
          affordance here is removed. Just the M-NNN identifier remains. */}
      <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px] me-text-mute">
        <span className="me-identifier me-mono uppercase">{code}</span>
        {/* Cadence status color — a label-less rounded rectangle between the
            M-number and the Add-material action; color only, hover for the count,
            click for the Global cadence colors editor (Ken 2026-08-19). */}
        <CadenceDot matter={matter} />
        {/* Attach a Google Doc from the attorney's Drive to this matter (Ken
            2026-08-18). Right of the code, under the name. Always visible (not
            me-identifier) since it's an action, not an identifier. */}
        <button
          onClick={() => setDriveOpen(true)}
          className="me-btn me-btn-outline !px-1.5 !py-0.5 !text-[10.5px] inline-flex items-center gap-1"
          title="Attach a Google Doc from your Drive to this matter"
        >
          {/* KEN (Ken 2026-10-23, #4): text-only "Add material" — the doc-plus
              glyph is dropped. MOVU keeps the glyph + "Add material from Drive". */}
          {mhPlayhead
            ? 'Add material'
            : <><Glyph name="add_from_drive" size={11}><FilePlus size={11} /></Glyph> Add material from Drive</>}
        </button>
      </div>
      <DriveMaterialModal
        matter={{ id: matter.id, code, name: matter.name }}
        open={driveOpen}
        onClose={() => setDriveOpen(false)}
      />
    </div>
  )
}

// MatterKindEditor + MATTER_KIND_OPTIONS removed (Emerson 2026-08-07) — the
// pane-2 `kind` chip is no longer shown or user-editable. The `kind` field
// (EmailIssue.kind / Matter.kind, vocabulary KIND_CHOICES_INFORMATIONAL, set
// at L3 Detect Issues) still exists on the model and drives downstream logic;
// it just has no display/edit affordance in this pane anymore.

// MatterFolderChips removed 2026-07-08 — folder controls (icon, chips,
// manage, archive) now live only in the folders sidebar per Ken's memo.

// MatterStageEditor removed 2026-05-25 — stage display + editing
// removed from pane 2 header (callout 8). The stage field stays in
// the DB and may be reintroduced later.

function ViewModeControls({
  style, isLocked, onStyleChange, hasTray, hasDocuments,
}: {
  style: TimelineStyle
  isLocked: boolean
  onStyleChange: (s: TimelineStyle, lock: boolean) => void
  hasTray: boolean
  hasDocuments: boolean
}) {
  const allOpts: Array<{ id: TimelineStyle; label: string; icon: React.ReactNode }> = [
    // Ken 2026-07-21 — 'Spine' → 'Phase' + ChevronsRight (>>>) icon;
    // 'Feed' → 'Chronology' + Menu (three horizontal lines) icon
    // (Ken 2026-08-18; label spelled out in full 2026-08-19). 'Docs' lists
    // the matter's attached documents (Ken
    // 2026-08-18). Internal TimelineStyle ids stay for backward-compat with
    // pinned settings.
    { id: 'spine',  label: glyphLabel('phases'), icon: <Glyph name="phases" size={10}><ChevronsRight size={10} /></Glyph> },
    { id: 'activity', label: glyphLabel('tray'), icon: <Glyph name="tray" size={10}><Layers size={10} /></Glyph> },
    { id: 'feed',   label: glyphLabel('chronology'),  icon: <Glyph name="chronology" size={10}><Menu size={10} /></Glyph> },
    { id: 'documents', label: glyphLabel('docs'), icon: <Glyph name="docs" size={10}><FileText size={10} /></Glyph> },
  ]
  // Hide Tray / Docs options for matters that don't offer them.
  let styleOpts = allOpts.filter(
    (o) => (o.id !== 'activity' || hasTray) && (o.id !== 'documents' || hasDocuments),
  )
  const auth = useAuth()
  const playhead = auth.status === 'authenticated' && isPlayheadTenant(auth.user.client?.code)
  if (playhead) {
    // pane2.toolbar.view_switcher (Ken 2026-10-25): the three view NAMES shown
    // INLINE — each its own click target that sets THAT view directly (no cycling,
    // no rounded container / border / background, no leading label). Order
    // Chronology / Phases / Docs; Docs only when the matter has documents.
    const items: Array<{ id: TimelineStyle; label: string }> = [
      { id: 'feed', label: glyphLabel('chronology') },
      { id: 'spine', label: glyphLabel('phases') },
      ...(hasDocuments ? ([{ id: 'documents' as TimelineStyle, label: glyphLabel('docs') }]) : []),
    ]
    // Type is the chrome/control token (.me-chrome-control / .is-active). NO
    // separators — a 20px gap is the only separation. me-pane1-size cancels the
    // pane-2 reading-zoom so these buttons sit at pane-1's size (Ken 2026-10-25).
    return (
      <div className="inline-flex items-baseline" style={{ gap: 20 }}>
        {items.map((o) => (
          <button
            key={o.id}
            type="button"
            onClick={() => onStyleChange(o.id, isLocked)}
            aria-pressed={style === o.id}
            className={`me-chrome-control${style === o.id ? ' is-active' : ''}`}
            style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
          >{o.label}</button>
        ))}
      </div>
    )
  }
  return (
    <>
      <div className="inline-flex overflow-hidden rounded-sm border me-border">
        {styleOpts.map((o) => {
          const active = style === o.id
          return (
            <button
              key={o.id}
              onClick={() => onStyleChange(o.id, isLocked)}
              data-selected={active}
              className="me-brand me-btn !rounded-none !text-[10px] !px-2 !py-1 inline-flex items-center gap-1"
              style={active ? {
                background: 'var(--me-selected)',
                color: 'var(--me-text)',
                fontWeight: 600,
              } : {}}
            >
              {o.icon}
              {o.label}
            </button>
          )
        })}
      </div>
      {/* Per-matter style lock hidden per Emerson 2026-08-05 — not needed
          now. The pin logic (setStyle/isLocked, me_matter_style) is left
          intact so existing pins still resolve and the control can be
          restored by re-rendering this button. */}
    </>
  )
}

function SortToggle({
  sortDir, onToggle, muted,
}: {
  sortDir: 'desc' | 'asc'
  onToggle: () => void
  /** KEN (SPEC-0001): borderless, muted — it sits at the head of the list, not on
   *  the switcher row. */
  muted?: boolean
}) {
  return (
    <button
      onClick={onToggle}
      title={sortDir === 'desc' ? 'Newest first — click for oldest first' : 'Oldest first — click for newest first'}
      aria-label={sortDir === 'desc' ? 'Sorted newest first' : 'Sorted oldest first'}
      className={muted
        ? 'me-brand inline-flex items-center !px-1 !py-0.5 me-text-mute hover:me-text'
        : 'me-brand me-btn me-btn-outline !text-[10px] !px-1.5 !py-1 inline-flex items-center gap-1'}
    >
      <Glyph name="pane2_sort_dir" size={10}>{sortDir === 'desc' ? <ArrowDown size={10} /> : <ArrowUp size={10} />}</Glyph>
    </button>
  )
}

// ─── Narrative-group bucketing ────────────────────────────────────────────
//
// narrate_matter_history emits a list of NarrativeGroups: themed phases of
// the matter, each owning a set of artifact_ids. Both the spine and stages
// views use these as the top-level units (instead of flat artifact lists),
// so the matter reads as a story rather than a stream.
//
// Properties used downstream:
//   - earliest:     min source_date across the group's artifacts (sort key)
//   - latest:       max source_date (display)
//   - artifacts:    resolved Artifact[] from the group's items[]
//   - dominantStage: the stage_id that the most artifacts in the group
//                    sit in (used to bucket groups under stages)
//   - notesByAid:   {artifact_id: note} for the per-row note, surfaced
//                    inline in the expanded view

type ResolvedGroup = {
  label: string
  summary: string
  artifacts: Artifact[]
  earliest: string  // ISO source_date or ''
  latest: string
  dominantStage: string  // stage_id or ''
  notesByAid: Record<number, string>
  /** Index of each artifact within group.items[] — used to build the
   *  manual-edit field path for the per-item note. Undefined for orphans. */
  itemIndexByAid: Record<number, number>
  /** Original index of the group as the AI returned it. The prompt asks
   *  for chronological order across groups, so this preserves that
   *  authorial order even when artifact-date sort would shuffle. */
  origIndex: number
}

function resolveGroups(
  groups: NarrativeGroup[] | undefined,
  artifacts: Artifact[],
): ResolvedGroup[] {
  if (!groups || groups.length === 0) return []
  const byId = new Map(artifacts.map((a) => [a.id, a]))
  return groups.map((g, idx) => {
    const items = Array.isArray(g.items) ? g.items : []
    const resolved: Artifact[] = []
    const notes: Record<number, string> = {}
    const itemIndexByAid: Record<number, number> = {}
    for (let i = 0; i < items.length; i++) {
      const it = items[i]
      const a = byId.get(it.artifact_id)
      if (a) {
        resolved.push(a)
        if (it.note) notes[it.artifact_id] = it.note
        itemIndexByAid[it.artifact_id] = i
      }
    }
    const dates = resolved.map((a) => a.source_date || '').filter(Boolean).sort()
    const stageCounts = new Map<string, number>()
    for (const a of resolved) {
      const s = ((a as any).stage as string) || ''
      stageCounts.set(s, (stageCounts.get(s) || 0) + 1)
    }
    let dominantStage = ''
    let max = 0
    for (const [s, n] of stageCounts) {
      if (s && n > max) { dominantStage = s; max = n }
    }
    return {
      label: g.label || '(unlabeled)',
      summary: g.summary || '',
      artifacts: resolved,
      earliest: dates[0] || '',
      latest: dates[dates.length - 1] || '',
      dominantStage,
      notesByAid: notes,
      itemIndexByAid,
      origIndex: idx,
    }
  }).filter((g) => g.artifacts.length > 0)
}

// Pipeline v2 variant: resolve phase_groupings (issue_id-based) by
// going through matter.email_issues to map each issue_id back to an
// artifact_id, then build the same ResolvedGroup shape so the rest of
// the timeline code (NarrativeGroupRow etc.) keeps working unchanged.
function resolvePhaseGroupings(
  phaseGroupings: NonNullable<Matter['phase_groupings']> | undefined,
  emailIssues: NonNullable<Matter['email_issues']> | undefined,
  artifacts: Artifact[],
): ResolvedGroup[] {
  if (!phaseGroupings || phaseGroupings.length === 0) return []
  const artifactById = new Map(artifacts.map((a) => [a.id, a]))
  const artifactIdByIssue = new Map(
    (emailIssues || []).map((ei) => [ei.issue_id, ei.artifact_id]),
  )
  return phaseGroupings.map((g, idx) => {
    const items = Array.isArray(g.items) ? g.items : []
    const resolved: Artifact[] = []
    const notes: Record<number, string> = {}
    const itemIndexByAid: Record<number, number> = {}
    for (let i = 0; i < items.length; i++) {
      const it = items[i]
      const aid = artifactIdByIssue.get(it.issue_id)
      if (aid == null) continue
      const a = artifactById.get(aid)
      if (!a) continue
      resolved.push(a)
      if (it.note) notes[aid] = it.note
      itemIndexByAid[aid] = i
    }
    const dates = resolved.map((a) => a.source_date || '').filter(Boolean).sort()
    return {
      label: g.phase_label || '(unlabeled)',
      summary: '',
      artifacts: resolved,
      earliest: dates[0] || '',
      latest: dates[dates.length - 1] || '',
      dominantStage: '',
      notesByAid: notes,
      itemIndexByAid,
      origIndex: idx,
    }
  }).filter((g) => g.artifacts.length > 0)
}

// v2 unfiled: artifacts in this matter's M2M that aren't referenced by
// any phase_groupings issue_id (or whose issue resolves to a different
// artifact). Mirrors the v1 unfiledGroup helper for the v2 data path.
function unfiledPhaseGroup(
  phaseGroupings: NonNullable<Matter['phase_groupings']> | undefined,
  emailIssues: NonNullable<Matter['email_issues']> | undefined,
  artifacts: Artifact[],
): ResolvedGroup | null {
  if (!phaseGroupings || phaseGroupings.length === 0) return null
  const artifactIdByIssue = new Map(
    (emailIssues || []).map((ei) => [ei.issue_id, ei.artifact_id]),
  )
  const idsInMatter = new Set(artifacts.map((a) => a.id))
  const filed = new Set<number>()
  for (const g of phaseGroupings) {
    for (const it of g.items || []) {
      const aid = artifactIdByIssue.get(it.issue_id)
      if (aid != null) filed.add(aid)
    }
  }
  // phase_groupings are issue_id-based, i.e. they reference EMAILS only —
  // an attachment is never named directly. So an attachment belongs to
  // whatever phase its parent email is in. Mark every child of a filed
  // email as filed too; otherwise the attachment wrongly falls through to
  // "Unfiled" even though its parent email is placed (this was dumping
  // every attachment of a filed email into Unfiled).
  for (const a of artifacts) {
    if (a.parent_artifact && idsInMatter.has(a.parent_artifact) && filed.has(a.parent_artifact)) {
      filed.add(a.id)
    }
  }
  const orphans = artifacts.filter((a) => !filed.has(a.id))
  if (orphans.length === 0) return null
  const dates = orphans.map((a) => a.source_date || '').filter(Boolean).sort()
  return {
    label: 'Unfiled',
    summary: 'Artifacts the latest Group Phases run did not place into a phase.',
    artifacts: orphans,
    earliest: dates[0] || '',
    latest: dates[dates.length - 1] || '',
    dominantStage: '',
    notesByAid: {},
    itemIndexByAid: {},
    origIndex: 9999,
  }
}

// Find artifacts that aren't referenced by any narrative group — they
// should still be visible (so users can see "this artifact came in but
// the AI hasn't grouped it yet"). Bucketed into a synthetic "Unfiled"
// pseudo-group for both views.
function unfiledGroup(
  groups: NarrativeGroup[] | undefined,
  artifacts: Artifact[],
): ResolvedGroup | null {
  if (!groups || groups.length === 0) return null
  const idsInMatter = new Set(artifacts.map((a) => a.id))
  const seen = new Set<number>()
  for (const g of groups) {
    for (const it of g.items || []) seen.add(it.artifact_id)
  }
  // Same rule as the v2 path: an attachment rides along with its parent
  // email's grouping, so don't strand it in Unfiled when the parent is filed.
  for (const a of artifacts) {
    if (a.parent_artifact && idsInMatter.has(a.parent_artifact) && seen.has(a.parent_artifact)) {
      seen.add(a.id)
    }
  }
  const orphans = artifacts.filter((a) => !seen.has(a.id))
  if (orphans.length === 0) return null
  const dates = orphans.map((a) => a.source_date || '').filter(Boolean).sort()
  return {
    label: 'Unfiled',
    summary: 'Artifacts the latest narrate run did not place into a phase.',
    artifacts: orphans,
    earliest: dates[0] || '',
    latest: dates[dates.length - 1] || '',
    dominantStage: '',
    notesByAid: {},
    itemIndexByAid: {},
    origIndex: 9999,  // sort to end
  }
}

// A single top-level artifact (an email) with its attachments — and any
// body-linked Google Docs — nested indented BELOW it. Same treatment Feed
// (List) and Activity (Tray) use, extracted so the Phase view renders
// parent→child the same way instead of flat-sorting attachments by date
// (which floated them ABOVE their parent email).
function ArtifactWithChildren({
  artifact, childrenList, matterId, selectedId, onSelect, showDetails, showChips,
  markupArtifactId = null, onOpenMarkup, onOpenChangeCarry,
}: {
  artifact: Artifact
  childrenList: Artifact[]
  matterId: number
  selectedId: number | null
  onSelect: (id: number | null) => void
  showDetails?: boolean
  showChips?: boolean
  markupArtifactId?: number | null
  onOpenMarkup?: (id: number) => void
  onOpenChangeCarry?: (data: ChangeCarry, filename: string) => void
}) {
  const linkedDocs = artifact.linked_docs || []
  return (
    <div>
      <TimelineArtifactRow
        artifact={artifact}
        matterId={matterId}
        selected={selectedId === artifact.id}
        onSelect={() => onSelect(artifact.id)}
        showChips={showChips}
        showDetails={showDetails}
        markupArtifactId={markupArtifactId}
        onOpenMarkup={onOpenMarkup} onOpenChangeCarry={onOpenChangeCarry}
      />
      {(childrenList.length > 0 || linkedDocs.length > 0) && (
        <div className="ml-6 pl-2 py-1 mb-1 rounded-md bg-[#fafaf9]">
          {childrenList.map((c) => (
            <TimelineArtifactRow
              key={c.id}
              artifact={c}
              matterId={matterId}
              selected={selectedId === c.id}
              onSelect={() => onSelect(c.id)}
              showChips={showChips}
              showDetails={showDetails}
              markupArtifactId={markupArtifactId}
              onOpenMarkup={onOpenMarkup} onOpenChangeCarry={onOpenChangeCarry}
            />
          ))}
          {linkedDocs.map((ld) => (
            <LinkedDocRow
              key={`ld-${ld.id}`}
              link={ld}
              selected={ld.doc_id != null && selectedId === ld.doc_id}
              onSelect={() => { if (ld.doc_id != null) onSelect(ld.doc_id) }}
              markupArtifactId={markupArtifactId}
              onOpenMarkup={onOpenMarkup} onOpenChangeCarry={onOpenChangeCarry}
            />
          ))}
        </div>
      )}
    </div>
  )
}

// ─── Timeline view: Spine ─────────────────────────────────────────────────

function SpineTimeline({
  artifacts, matter, selectedId, onSelect, showDetails, sortDir,
  markupArtifactId = null, onOpenMarkup, onOpenChangeCarry,
}: {
  artifacts: Artifact[]
  matter: Matter
  selectedId: number | null
  onSelect: (id: number | null) => void
  showDetails?: boolean
  sortDir: 'desc' | 'asc'
  markupArtifactId?: number | null
  onOpenMarkup?: (id: number) => void
  onOpenChangeCarry?: (data: ChangeCarry, filename: string) => void
}) {
  // Parent→children map across the WHOLE matter artifact list (built once,
  // like Feed/Tray). An attachment nests under its parent email wherever
  // that email is rendered — inside its phase, or inside Unfiled.
  const idsInMatter = useMemo(() => new Set(artifacts.map((a) => a.id)), [artifacts])
  const childrenByParent = useMemo(() => {
    const m = new Map<number, Artifact[]>()
    for (const a of artifacts) {
      if (a.parent_artifact && idsInMatter.has(a.parent_artifact)) {
        const arr = m.get(a.parent_artifact) || []
        arr.push(a)
        m.set(a.parent_artifact, arr)
      }
    }
    return m
  }, [artifacts, idsInMatter])
  const groups = useMemo(() => {
    // Pipeline v2: prefer phase_groupings (issue_id-based) when present.
    // Falls back to v1 narrative_groups for matters never re-run on v2.
    const usingV2 = !!(matter.phase_groupings && matter.phase_groupings.length > 0)
    const resolved = usingV2
      ? resolvePhaseGroupings(matter.phase_groupings, matter.email_issues, artifacts)
      : resolveGroups(matter.narrative_groups, artifacts)
    const unfiled = usingV2
      ? unfiledPhaseGroup(matter.phase_groupings, matter.email_issues, artifacts)
      : unfiledGroup(matter.narrative_groups, artifacts)
    if (unfiled) resolved.push(unfiled)
    // Authorial order from the AI when ascending; reversed for newest-first.
    return sortDir === 'asc' ? resolved : [...resolved].reverse()
  }, [matter.phase_groupings, matter.email_issues, matter.narrative_groups, artifacts, sortDir])

  // No groups → fall back to flat artifact spine (e.g. matter has no
  // narrate run yet, or all artifacts are unfiled). Still nest attachments
  // under their parent email rather than interleaving them by date.
  if (groups.length === 0) {
    const topLevel = artifacts.filter((a) => !a.parent_artifact || !idsInMatter.has(a.parent_artifact))
    return (
      <div>
        {topLevel.map((a) => (
          <ArtifactWithChildren
            key={a.id}
            artifact={a}
            childrenList={childrenByParent.get(a.id) || []}
            matterId={matter.id}
            selectedId={selectedId}
            onSelect={onSelect}
            showDetails={showDetails}
            markupArtifactId={markupArtifactId}
            onOpenMarkup={onOpenMarkup} onOpenChangeCarry={onOpenChangeCarry}
          />
        ))}
      </div>
    )
  }

  return (
    <div>
      {groups.map((g) => (
        <NarrativeGroupRow
          key={`spine-${g.origIndex}-${g.label}`}
          group={g}
          matterId={matter.id}
          childrenByParent={childrenByParent}
          idsInMatter={idsInMatter}
          selectedId={selectedId}
          onSelect={onSelect}
          showDetails={showDetails}
          artifactsSortDir={sortDir}
          markupArtifactId={markupArtifactId}
          onOpenMarkup={onOpenMarkup} onOpenChangeCarry={onOpenChangeCarry}
        />
      ))}
    </div>
  )
}

// ─── Narrative-group row (collapsed by default; expand to see artifacts) ──

function NarrativeGroupRow({
  group, matterId, childrenByParent, idsInMatter,
  selectedId, onSelect, showSpine, showDetails, artifactsSortDir,
  markupArtifactId = null, onOpenMarkup, onOpenChangeCarry,
}: {
  group: ResolvedGroup
  matterId: number
  childrenByParent: Map<number, Artifact[]>
  idsInMatter: Set<number>
  selectedId: number | null
  onSelect: (id: number | null) => void
  showSpine?: boolean
  showDetails?: boolean
  artifactsSortDir: 'desc' | 'asc'
  markupArtifactId?: number | null
  onOpenMarkup?: (id: number) => void
  onOpenChangeCarry?: (data: ChangeCarry, filename: string) => void
}) {
  // KEN (Ken 2026-10-23, #13/#14/#15): on the playhead the phase row loses its
  // disclosure chevron (the whole row is the toggle target, like the pane-1
  // categories) and its per-phase metadata moves to a fixed right-aligned column
  // in muted sans. MOVU keeps the chevron + the mono inline metadata.
  const ngAuth = useAuth()
  const ngPlayhead = ngAuth.status === 'authenticated' && isPlayheadTenant(ngAuth.user.client?.code)
  // Auto-expand if this group contains the selected artifact — including a
  // selected attachment nested under one of the group's emails (so a
  // selection from search / chat lands on a visible row).
  const containsSelected = selectedId != null
    && group.artifacts.some((a) =>
      a.id === selectedId
      || (childrenByParent.get(a.id) || []).some((c) => c.id === selectedId))
  const [open, setOpen] = useState<boolean>(containsSelected)
  useEffect(() => {
    if (containsSelected) setOpen(true)
  }, [containsSelected])
  const toggleOpen = () => {
    setOpen((wasOpen) => {
      const willClose = wasOpen
      // Collapsing a group that contained the user's selected artifact
      // also clears the selection — feels like the artifact "went away
      // with the group" rather than staying highlighted in a now-hidden
      // row, and matches the empty right-pane state of fresh matter entry.
      if (willClose && containsSelected) onSelect(null)
      return !wasOpen
    })
  }

  // Only TOP-LEVEL artifacts (emails) are rows here; their attachments nest
  // beneath via childrenByParent. Filtering to top-level also guards against
  // double-rendering an attachment that a group happened to list directly.
  const topLevel = useMemo(() => {
    const tops = group.artifacts.filter(
      (a) => !a.parent_artifact || !idsInMatter.has(a.parent_artifact),
    )
    return tops.sort((a, b) => {
      const ad = a.source_date || ''
      const bd = b.source_date || ''
      const primary = artifactsSortDir === 'desc' ? bd.localeCompare(ad) : ad.localeCompare(bd)
      if (primary !== 0) return primary
      // Tie-break on client_seq so same-second arrivals still swap when
      // the user flips sort direction. Falls back to id for older rows
      // that predate per-client sequencing.
      const aSeq = a.client_seq ?? a.id
      const bSeq = b.client_seq ?? b.id
      return artifactsSortDir === 'desc' ? bSeq - aSeq : aSeq - bSeq
    })
  }, [group.artifacts, idsInMatter, artifactsSortDir])

  // Two counts, not one combined "artifacts" number: emails (the top-level
  // rows) and attachments (their nested children). Documents, if any ever
  // top-level, get their own segment too.
  const { emailCount, attachmentCount, docCount } = useMemo(() => {
    let e = 0, at = 0, d = 0
    const tally = (a: Artifact) => {
      if (a.artifact_type === 'attachment') at += 1
      else if (a.artifact_type === 'document') d += 1
      else e += 1
    }
    for (const a of topLevel) {
      tally(a)
      for (const c of childrenByParent.get(a.id) || []) tally(c)
    }
    return { emailCount: e, attachmentCount: at, docCount: d }
  }, [topLevel, childrenByParent])

  // Two-level structure (so the expanded artifacts area is a SIBLING
  // of the flex row, NOT a child — earlier shape made the expanded
  // area a flex child that stole all available width and collapsed
  // the label/summary column to 0px, causing character-by-character
  // wrap):
  //   <wrapper-div>
  //     <header-row role=button display=flex>  (toggle target)
  //       chevron, label/summary stack
  //     </header-row>
  //     {open && <artifacts-area>}             (sibling, full width)
  //   </wrapper-div>
  return (
    <div className="mb-1.5">
      <div
        onClick={toggleOpen}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if ((e.key === 'Enter' || e.key === ' ') && e.target === e.currentTarget) {
            e.preventDefault(); toggleOpen()
          }
        }}
        className="me-row w-full text-left relative cursor-pointer"
        style={{ padding: '8px 10px', display: 'flex', alignItems: 'flex-start', gap: 8 }}
      >
        {showSpine && (
          <div
            className="absolute"
            style={{
              left: -19,
              top: 14,
              width: 10, height: 10,
              borderRadius: 5,
              background: 'var(--me-surface)',
              border: '1.5px solid var(--me-borderStrong, var(--me-border))',
            }}
          />
        )}
        {/* KEN (Ken 2026-10-23, #15): no disclosure chevron — the whole row is the
            toggle target, matching the pane-1 categories (which deliberately have
            none). No replacement icon. MOVU keeps the chevron. */}
        {!ngPlayhead && (open
          ? <ChevronDown size={13} className="me-text-mute mt-0.5 shrink-0" />
          : <ChevronRight size={13} className="me-text-mute mt-0.5 shrink-0" />
        )}
        <div className="flex-1 min-w-0">
          {/* Per Ken 2026-05-25 (corrected): narrative-group label/header
              STAYS as the bold title — it's the navigation orienter.
              Pencil-edit removed since labels come straight from the AI
              now. The per-artifact `note` rendering (the italicized
              line on each artifact row) is what's removed; see
              TimelineArtifactRow below. */}
          {ngPlayhead ? (
            // KEN (#13/#14): per-phase metadata → a fixed right-aligned column in
            // muted SANS (not mono), counts over date-range so the column stays
            // narrow and lands at the same x on every row. The label takes the
            // rest of the row and WRAPS instead of truncating, so a long label
            // like "Case opening…" no longer clips (#14 falls out of #13).
            <div className="flex items-start gap-2 min-w-0">
              <span className="font-semibold me-text flex-1 min-w-0 text-[12.5px]">
                {group.label || <span className="me-text-mute italic">(unlabeled)</span>}
              </span>
              <span className="flex flex-col items-end text-right text-[10.5px] me-text-mute shrink-0 leading-tight">
                <span className="whitespace-nowrap">
                  {emailCount} email{emailCount === 1 ? '' : 's'}
                  {attachmentCount > 0 && `, ${attachmentCount} attachment${attachmentCount === 1 ? '' : 's'}`}
                  {docCount > 0 && `, ${docCount} document${docCount === 1 ? '' : 's'}`}
                </span>
                {group.earliest && (
                  <span className="whitespace-nowrap">
                    {fmtDateShort(group.earliest)}
                    {group.latest && group.latest !== group.earliest && ` → ${fmtDateShort(group.latest)}`}
                  </span>
                )}
              </span>
            </div>
          ) : (
            <div className="flex items-baseline gap-2 min-w-0">
              <span className="font-semibold me-text truncate flex-1 min-w-0 text-[12.5px]">
                {group.label || <span className="me-text-mute italic">(unlabeled)</span>}
              </span>
              <span className="me-mono text-[10.5px] me-text-mute shrink-0">
                {emailCount} email{emailCount === 1 ? '' : 's'}
                {attachmentCount > 0 && `, ${attachmentCount} attachment${attachmentCount === 1 ? '' : 's'}`}
                {docCount > 0 && `, ${docCount} document${docCount === 1 ? '' : 's'}`}
              </span>
              {group.earliest && (
                <span className="me-mono text-[10.5px] me-text-mute shrink-0">
                  {fmtDateShort(group.earliest)}
                  {group.latest && group.latest !== group.earliest && ` → ${fmtDateShort(group.latest)}`}
                </span>
              )}
            </div>
          )}
          {/* Ken 2026-07-21 — phase summaries removed from Phase view
              (formerly Spine). The label + counts + date range are
              enough at this granularity; per-phase prose lived here
              historically but Ken called it noise. */}
        </div>
      </div>{/* header-row close */}
      {open && (
        <div className="ml-5 mt-1 pl-3 border-l me-border">
          {topLevel.map((a) => (
            <ArtifactWithChildren
              key={a.id}
              artifact={a}
              childrenList={childrenByParent.get(a.id) || []}
              matterId={matterId}
              selectedId={selectedId}
              onSelect={onSelect}
              showDetails={showDetails}
              markupArtifactId={markupArtifactId}
              onOpenMarkup={onOpenMarkup} onOpenChangeCarry={onOpenChangeCarry}
            />
          ))}
        </div>
      )}
    </div>
  )
}

// ─── Timeline view: Activity (formerly Stages) ────────────────────────────
//
// Pipeline v2: replaces the old static stage-template view. Dividers come
// from matter.activity_index — the output of "Categorize Issues by Legal
// Activity Type" — so categories are the AI's per-matter labels (e.g.
// DRAFT AND NEGOTIATION, EXECUTION), not the hardcoded INTAKE & SCOPING
// / DILIGENCE / etc. Only categories with at least one item are rendered.
// No sequential numbering, no separator dots — just the label, an item
// count, and the artifact rows beneath in chronological order.

function ActivityTimeline({
  artifacts, matter, selectedId, onSelect, sortDir, showDetails,
  markupArtifactId = null, onOpenMarkup, onOpenChangeCarry,
}: {
  artifacts: Artifact[]
  matter: Matter
  selectedId: number | null
  onSelect: (id: number | null) => void
  sortDir: 'desc' | 'asc'
  showDetails?: boolean
  markupArtifactId?: number | null
  onOpenMarkup?: (id: number) => void
  onOpenChangeCarry?: (data: ChangeCarry, filename: string) => void
}) {
  const artifactIdByIssue = useMemo(() => {
    const m = new Map<string, number>()
    for (const ei of matter.email_issues || []) m.set(ei.issue_id, ei.artifact_id)
    return m
  }, [matter.email_issues])
  const artifactById = useMemo(
    () => new Map(artifacts.map((a) => [a.id, a])),
    [artifacts],
  )
  // Emerson 2026-07-21 — Tray view should nest child attachments
  // under their parent email exactly like Feed does. Build the
  // parent→children map ONCE across the full matter artifact list
  // (not per-category, so the same attachments render under whichever
  // parent's category surfaces the email). `idsInMatter` mirrors
  // Feed's guard so we don't try to nest a child whose parent isn't
  // on this matter's M2M (defensive; the M2M should always contain
  // the parent when it contains the child).
  const idsInMatter = useMemo(() => new Set(artifacts.map((a) => a.id)), [artifacts])
  const childrenByParent = useMemo(() => {
    const m = new Map<number, Artifact[]>()
    for (const a of artifacts) {
      if (a.parent_artifact && idsInMatter.has(a.parent_artifact)) {
        const arr = m.get(a.parent_artifact) || []
        arr.push(a)
        m.set(a.parent_artifact, arr)
      }
    }
    return m
  }, [artifacts, idsInMatter])
  // Resolve each activity_index category to its non-empty artifact list.
  const categories = useMemo(() => {
    const out: Array<{ label: string; artifacts: Artifact[] }> = []
    // Content-free followups (A′) never sit in the LLM categories — they get
    // their own derived Tray category (e.g. "Preservation Notices"). Ken 2026-08-23.
    const followups = artifacts.filter((a) => !!a.followup_kind)
    const followupIds = new Set(followups.map((a) => a.id))
    const bySrc = (a: Artifact, b: Artifact) => {
      const ad = a.source_date || ''
      const bd = b.source_date || ''
      return sortDir === 'desc' ? bd.localeCompare(ad) : ad.localeCompare(bd)
    }
    for (const cat of matter.activity_index || []) {
      const items = Array.isArray(cat.items) ? cat.items : []
      const resolved: Artifact[] = []
      const seen = new Set<number>()
      for (const it of items) {
        const aid = artifactIdByIssue.get(it.email_issue_id)
        if (aid == null) continue
        const a = artifactById.get(aid)
        if (!a || seen.has(a.id) || followupIds.has(a.id)) continue
        seen.add(a.id)
        resolved.push(a)
      }
      if (resolved.length === 0) continue
      resolved.sort(bySrc)  // chronological within category, honoring sort dir
      out.push({ label: cat.category_label || '(unlabeled)', artifacts: resolved })
    }
    // Append synthetic followup categories, grouped by their derived label.
    const byLabel = new Map<string, Artifact[]>()
    for (const a of followups) {
      const label = a.followup_label || 'Receipts & auto-replies'
      const arr = byLabel.get(label) || []
      arr.push(a)
      byLabel.set(label, arr)
    }
    for (const [label, arr] of byLabel) {
      arr.sort(bySrc)
      out.push({ label, artifacts: arr })
    }
    return out
  }, [matter.activity_index, artifactIdByIssue, artifactById, sortDir, artifacts])

  // Per-matter collapse state for the category sections. Loaded from
  // localStorage; re-synced when the pane is reused for a different matter.
  // (Declared before the early returns below so the hooks always run.)
  const [collapsed, setCollapsed] = useState<Set<string>>(() => loadTrayCollapsed(matter.id))
  useEffect(() => { setCollapsed(loadTrayCollapsed(matter.id)) }, [matter.id])
  const persistCollapsed = (next: Set<string>) => {
    setCollapsed(next)
    saveTrayCollapsed(matter.id, next)
  }

  if (categories.length === 0 && (matter.activity_index || []).length === 0) {
    return (
      <div className="px-2 py-3 text-[11.5px] italic me-text-mute">
        No activity categories yet. Categorize Issues by Activity Type hasn't
        run for this matter (it fires only for litigation / contract matters).
      </div>
    )
  }
  if (categories.length === 0) {
    return (
      <div className="px-2 py-3 text-[11.5px] italic me-text-mute">
        Activity categories exist but no issues could be resolved to artifacts
        in this matter.
      </div>
    )
  }

  // Expand-all / collapse-all toggle: only meaningful with >1 category.
  // "Collapse all" also clears any selection so a selected-forced-open
  // section actually closes (see the per-category contains-selected logic).
  const allLabels = categories.map((c) => c.label)
  const allCollapsed = allLabels.length > 0 && allLabels.every((l) => collapsed.has(l))
  const collapseAll = () => { onSelect(null); persistCollapsed(new Set(allLabels)) }
  const expandAll = () => persistCollapsed(new Set())

  return (
    <div>
      {categories.length > 1 && (
        <div className="mb-2 flex justify-end">
          <button
            type="button"
            onClick={allCollapsed ? expandAll : collapseAll}
            className="text-[10px] tracking-wider uppercase me-text-mute hover:me-text cursor-pointer"
          >
            {allCollapsed ? 'expand all' : 'collapse all'}
          </button>
        </div>
      )}
      {categories.map((cat) => {
        // A collapsed section is force-opened while it holds the selected
        // artifact (or one of its nested children), so a selection arriving
        // from search / chat is never hidden. Toggling such a section closed
        // clears the selection so it can actually collapse.
        const containsSelected = selectedId != null && cat.artifacts.some((a) =>
          a.id === selectedId
          || (childrenByParent.get(a.id) || []).some((c) => c.id === selectedId)
        )
        const isOpen = !collapsed.has(cat.label) || containsSelected
        const toggle = () => {
          const next = new Set(collapsed)
          if (isOpen) {
            next.add(cat.label)
            if (containsSelected) onSelect(null)
          } else {
            next.delete(cat.label)
          }
          persistCollapsed(next)
        }
        return (
        <div key={cat.label} className="mb-4">
          <div
            onClick={toggle}
            role="button"
            tabIndex={0}
            aria-expanded={isOpen}
            onKeyDown={(e) => {
              if ((e.key === 'Enter' || e.key === ' ') && e.target === e.currentTarget) {
                e.preventDefault(); toggle()
              }
            }}
            className="mb-1.5 flex items-center gap-2 text-[10.5px] font-semibold tracking-wider uppercase me-text-mute cursor-pointer select-none"
          >
            {isOpen
              ? <ChevronDown size={12} className="shrink-0" />
              : <ChevronRight size={12} className="shrink-0" />
            }
            <span>{cat.label}</span>
            <div className="flex-1 h-px" style={{ background: 'var(--me-border)' }} />
            <span className="me-mono">{cat.artifacts.length}</span>
          </div>
          {isOpen && cat.artifacts.map((a) => {
            const kids = childrenByParent.get(a.id) || []
            const linkedDocs = a.linked_docs || []
            return (
              <div key={a.id}>
                <TimelineArtifactRow
                  artifact={a}
                  matterId={matter.id}
                  selected={selectedId === a.id}
                  onSelect={() => onSelect(a.id)}
                  showChips
                  showDetails={showDetails}
                  markupArtifactId={markupArtifactId}
                  onOpenMarkup={onOpenMarkup} onOpenChangeCarry={onOpenChangeCarry}
                />
                {/* Emerson 2026-07-21 — same nested-attachments treatment
                    Feed uses: ml-6 + dashed left border + linked_docs
                    included after real attachments. */}
                {(kids.length > 0 || linkedDocs.length > 0) && (
                  <div className="ml-6 pl-2 py-1 mb-1 rounded-md bg-[#fafaf9]">
                    {kids.map((c) => (
                      <TimelineArtifactRow
                        key={c.id}
                        artifact={c}
                        matterId={matter.id}
                        selected={selectedId === c.id}
                        onSelect={() => onSelect(c.id)}
                        showChips
                        showDetails={showDetails}
                        markupArtifactId={markupArtifactId}
                        onOpenMarkup={onOpenMarkup} onOpenChangeCarry={onOpenChangeCarry}
                      />
                    ))}
                    {linkedDocs.map((ld) => (
                      <LinkedDocRow
                        key={`ld-${ld.id}`}
                        link={ld}
                        selected={ld.doc_id != null && selectedId === ld.doc_id}
                        onSelect={() => { if (ld.doc_id != null) onSelect(ld.doc_id) }}
                        markupArtifactId={markupArtifactId}
                        onOpenMarkup={onOpenMarkup} onOpenChangeCarry={onOpenChangeCarry}
                      />
                    ))}
                  </div>
                )}
              </div>
            )
          })}
        </div>
        )
      })}
    </div>
  )
}

// ─── Timeline view: Documents ─────────────────────────────────────────────
// Ken 2026-08-18: a clean flat list of the matter's DOCUMENT artifacts — the
// home for docs attached via "Add material from Drive" so they don't scatter
// into the Unfiled bucket of the Phase / Tray views.
function DocumentsTimeline({
  artifacts, matterId, selectedId, onSelect, showDetails,
  markupArtifactId = null, onOpenMarkup, onOpenChangeCarry,
}: {
  artifacts: Artifact[]
  matterId: number
  selectedId: number | null
  onSelect: (id: number | null) => void
  showDetails?: boolean
  markupArtifactId?: number | null
  onOpenMarkup?: (id: number) => void
  onOpenChangeCarry?: (data: ChangeCarry, filename: string) => void
}) {
  // Docs view, versioned mode (Ken T6 #4): when the matter has attachments with a
  // Doc Version, show them as a two-column back-and-forth — Ridgemont (v0, v3) |
  // Northlane (v1, v2) — one doc per row in time order. Each card: version label,
  // filename, date, from→to, markup summary. Click opens pane-3. No carry result.
  const versioned = artifacts.filter((a) => a.artifact_type === 'attachment' && a.doc_version)
  if (versioned.length > 0) {
    const parseDV = (dv: string) => {
      const p = dv.split('·').map((x) => x.trim())
      return { v: p[0] || '', whose: p[1] || '', markup: p[2] || '', date: p[3] || '' }
    }
    const sorted = [...versioned].sort((a, b) => (a.source_date || '').localeCompare(b.source_date || ''))
    const verCard = (a: Artifact) => {
      const p = parseDV(a.doc_version || '')
      const side = (a.doc_side || '').toLowerCase()
      const dir = side === 'ridgemont' ? 'Ridgemont → Northlane'
        : p.v === 'v1' ? 'Northlane (internal)' : 'Northlane → Ridgemont'
      return (
        <button
          type="button"
          onClick={() => onSelect(a.id)}
          className="w-full text-left me-card p-2.5 hover:bg-[color:var(--me-panel)]"
          style={{ background: selectedId === a.id ? 'var(--me-selected)' : undefined }}
        >
          <div className="flex items-baseline gap-2">
            <span className="me-chip text-[10px] font-medium">{p.v}</span>
            {p.date && <span className="me-text-mute text-[10.5px]">{p.date}</span>}
          </div>
          <div className="mt-1 text-[12px] me-text font-medium break-words">{a.subject}</div>
          <div className="mt-0.5 text-[11px] me-text-mute">{dir}</div>
          {p.markup && <div className="mt-0.5 text-[11px] me-text-dim">{p.markup}</div>}
        </button>
      )
    }
    return (
      <div className="flex flex-col gap-2">
        <div className="grid grid-cols-2 gap-3 text-[10px] uppercase tracking-wider me-text-mute">
          <div>Ridgemont</div><div>Northlane</div>
        </div>
        {sorted.map((a) => {
          const isR = (a.doc_side || '').toLowerCase() === 'ridgemont'
          return (
            <div key={a.id} className="grid grid-cols-2 gap-3 items-start">
              {isR ? verCard(a) : <div />}
              {!isR ? verCard(a) : <div />}
            </div>
          )
        })}
      </div>
    )
  }

  // Docs view = the matter's artifacts grouped by Doc Type (Ken 2026-10-21). An
  // artifact lands under EVERY type it carries (multi-membership). Group headers
  // follow a canonical litigation order; any unlisted type is appended; standalone
  // documents with no tag fall into "Documents".
  const DOC_TYPE_ORDER = [
    'Counsel advice', 'Internal tasking', 'Hold notice', 'Filing', 'Tribunal notice',
    'Exhibit', 'Draft filing', 'Privileged assessment', 'Invoice', 'Scheduling', 'Client request',
  ]
  const byDate = (a: Artifact, b: Artifact) => (a.source_date || '').localeCompare(b.source_date || '')
  const tagged = artifacts.filter((a) => (a.doc_types?.length ?? 0) > 0)
  const untaggedDocs = artifacts.filter((a) => (a.doc_types?.length ?? 0) === 0 && a.artifact_type === 'document')

  if (tagged.length === 0 && untaggedDocs.length === 0) {
    return (
      <div className="rounded-sm border border-dashed me-border p-3 text-[12px] me-text-mute">
        No documents attached to this matter yet.
      </div>
    )
  }

  const present = [...new Set(tagged.flatMap((a) => a.doc_types || []))]
  const ordered = [
    ...DOC_TYPE_ORDER.filter((t) => present.includes(t)),
    ...present.filter((t) => !DOC_TYPE_ORDER.includes(t)),
  ]
  const groups: Array<{ label: string; items: Artifact[] }> = ordered.map((t) => ({
    label: t,
    items: tagged.filter((a) => (a.doc_types || []).includes(t)).sort(byDate),
  }))
  if (untaggedDocs.length) groups.push({ label: 'Documents', items: [...untaggedDocs].sort(byDate) })

  return (
    <div className="flex flex-col gap-3">
      {groups.map((g) => (
        <div key={g.label}>
          <div className="me-section-h mb-1 flex items-center gap-2">
            <span>{g.label}</span>
            <span className="me-text-mute tabular-nums">{g.items.length}</span>
          </div>
          {g.items.map((a) => (
            <TimelineArtifactRow
              key={`${g.label}:${a.id}`}
              artifact={a}
              matterId={matterId}
              selected={selectedId === a.id}
              onSelect={() => onSelect(a.id)}
              showChips
              showDetails={showDetails}
              markupArtifactId={markupArtifactId}
              onOpenMarkup={onOpenMarkup} onOpenChangeCarry={onOpenChangeCarry}
            />
          ))}
        </div>
      ))}
    </div>
  )
}

// ─── Timeline view: Feed ──────────────────────────────────────────────────

function FeedTimeline({
  artifacts, matterId, selectedId, onSelect, showDetails,
  markupArtifactId = null, onOpenMarkup, onOpenChangeCarry,
}: {
  artifacts: Artifact[]
  matterId: number
  selectedId: number | null
  onSelect: (id: number | null) => void
  showDetails?: boolean
  markupArtifactId?: number | null
  onOpenMarkup?: (id: number) => void
  onOpenChangeCarry?: (data: ChangeCarry, filename: string) => void
}) {
  // Nest child attachments under their parent email. matter.artifacts
  // contains both the email and its attachments (mirrored by runner_v2);
  // group by parent_artifact id so an attachment renders indented below
  // its parent. Top-level artifacts (no parent OR parent not in this
  // matter's M2M) render as their own rows.
  const idsInMatter = new Set(artifacts.map((a) => a.id))
  const topLevel = artifacts.filter((a) => !a.parent_artifact || !idsInMatter.has(a.parent_artifact))
  const childrenByParent = new Map<number, Artifact[]>()
  for (const a of artifacts) {
    if (a.parent_artifact && idsInMatter.has(a.parent_artifact)) {
      const arr = childrenByParent.get(a.parent_artifact) || []
      arr.push(a)
      childrenByParent.set(a.parent_artifact, arr)
    }
  }
  // Chronology stays strict date order (Ken T7 #1) — attachments still nest
  // under their parent email; conversation grouping lives in Phases, not here.
  return (
    <div>
      {topLevel.map((a) => {
        const kids = childrenByParent.get(a.id) || []
        return (
          <div key={a.id}>
            <TimelineArtifactRow
              artifact={a}
              matterId={matterId}
              selected={selectedId === a.id}
              onSelect={() => onSelect(a.id)}
              showChips
              showDetails={showDetails}
              markupArtifactId={markupArtifactId}
              onOpenMarkup={onOpenMarkup} onOpenChangeCarry={onOpenChangeCarry}
            />
            {(kids.length > 0 || (a.linked_docs && a.linked_docs.length > 0)) && (
              <div className="ml-6 pl-2 py-1 mb-1 rounded-md bg-[#fafaf9]">
                {kids.map((c) => (
                  <TimelineArtifactRow
                    key={c.id}
                    artifact={c}
                    matterId={matterId}
                    selected={selectedId === c.id}
                    onSelect={() => onSelect(c.id)}
                    showChips
                    showDetails={showDetails}
                    markupArtifactId={markupArtifactId}
                    onOpenMarkup={onOpenMarkup} onOpenChangeCarry={onOpenChangeCarry}
                  />
                ))}
                {/* Ken 2026-07-03: Google Doc URLs referenced in the
                    email body render as pseudo-attachment children.
                    Resolved links (doc synced from Drive) navigate to
                    the doc artifact when clicked, same as any
                    attachment. Unresolved stubs render with a
                    "not synced" note + an external Open in Docs
                    link so the user can still access content. */}
                {(a.linked_docs || []).map((ld) => (
                  <LinkedDocRow
                    key={`ld-${ld.id}`}
                    link={ld}
                    selected={ld.doc_id != null && selectedId === ld.doc_id}
                    onSelect={() => { if (ld.doc_id != null) onSelect(ld.doc_id) }}
                    markupArtifactId={markupArtifactId}
                    onOpenMarkup={onOpenMarkup} onOpenChangeCarry={onOpenChangeCarry}
                  />
                ))}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

// Linked-doc label ladder (Emerson 2026-08-07): per media_kind, the metadata
// token, the friendly fallback label (shown when there's no real name), and
// the type icon. Never let the raw drive_file_id be the visible label.
const LINKED_MEDIA = {
  image: { token: 'image', friendly: 'Drive image', Icon: ImageIcon },
  video: { token: 'video', friendly: 'Drive video', Icon: Video },
  doc: { token: 'gdoc', friendly: 'Google Doc', Icon: FileText },
  sheet: { token: 'gsheet', friendly: 'Google Sheet', Icon: Sheet },
  file: { token: 'gfile', friendly: 'Drive file', Icon: FileIcon },
} as const

function LinkedDocRow({
  link, selected, onSelect, onOpenMarkup,
}: {
  link: import('../api').LinkedDoc
  selected: boolean
  onSelect: () => void
  markupArtifactId?: number | null
  onOpenMarkup?: (id: number) => void
  onOpenChangeCarry?: (data: ChangeCarry, filename: string) => void
}) {
  // Google-native external URLs for the "Open in Docs / Sheets" fallback
  // (identical construction to BodyPreview.googleAppUrl). Used when the
  // doc isn't synced from Drive yet — the user can still get to the
  // content via the browser. `docs.google.com/file/` isn't a real path;
  // Drive-hosted uploads use `drive.google.com/file/d/`.
  const externalUrl = (
    link.doc_kind === 'document'    ? `https://docs.google.com/document/d/${link.drive_file_id}/edit` :
    link.doc_kind === 'spreadsheet' ? `https://docs.google.com/spreadsheets/d/${link.drive_file_id}/edit` :
    /* file */                        `https://drive.google.com/file/d/${link.drive_file_id}/view`
  )
  const isResolved = link.is_resolved && link.doc_id != null
  const media = LINKED_MEDIA[link.media_kind] || LINKED_MEDIA.file
  return (
    <div
      onClick={onSelect}
      role={isResolved ? 'button' : undefined}
      tabIndex={isResolved ? 0 : undefined}
      onKeyDown={(e) => {
        if (isResolved && (e.key === 'Enter' || e.key === ' ')) {
          if (e.target === e.currentTarget) { e.preventDefault(); onSelect() }
        }
      }}
      data-selected={selected ? 'true' : undefined}
      data-artifact-id={isResolved && link.doc_id != null ? link.doc_id : undefined}
      className={`me-row mb-0.5 w-full gap-2.5 text-left relative ${isResolved ? 'cursor-pointer' : ''}`}
      style={{ padding: '8px 10px', display: 'flex' }}
      title={isResolved
        ? 'Linked from the email body — click to open the doc'
        : 'Linked from the email body but not synced from Drive yet — open in browser'}
    >
      {/* Markup pip relocated to the meta line below (next to "open in docs"),
          per Emerson 2026-08-07. */}
      <Link2 size={13} className="me-text-mute mt-0.5 shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2 min-w-0">
          <span className="font-medium me-text flex-1 min-w-0 text-[12.5px] truncate">
            {/* Ladder: resolved subject → filename from "shared with you"
                subject → friendly type. The raw drive_file_id is never the
                label; it lives on the hover title + the "open in" link. */}
            {link.doc_subject || link.doc_name || (
              <span className="italic font-normal me-text-mute" title={link.drive_file_id}>
                {media.friendly}
              </span>
            )}
          </span>
          {!isResolved && (
            <span className="me-mono text-[10px] me-text-mute shrink-0">not synced</span>
          )}
        </div>
        {/* Meta line — pip + markup + "open in …". The gdoc icon/token line was
            removed; the markup pip now lives here and "markup" matches the
            "open in" size (both inherit text-[10.5px]). */}
        <div className="mt-0.5 flex items-center gap-1.5 text-[10.5px] me-text-mute">
          {/* Pip below the filename at the left; "markup" moved to the end so
              the pip isn't sitting directly beside it. */}
          {link.doc_has_markup && (isResolved && link.doc_id != null ? (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onOpenMarkup?.(link.doc_id!) }}
              title="View redlines + comments"
              className="-m-1.5 p-1.5 inline-flex items-center shrink-0"
            >
              <span aria-hidden style={{ width: 7, height: 7, borderRadius: '50%', background: '#BA7517', display: 'block' }} />
            </button>
          ) : (
            <span
              aria-hidden
              title="This document has redlines and/or comments"
              style={{ width: 7, height: 7, borderRadius: '50%', background: '#BA7517', flexShrink: 0 }}
            />
          ))}
          <a
            href={externalUrl}
            target="_blank"
            rel="noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="underline underline-offset-2 decoration-dotted hover:me-text"
          >
            open in {link.doc_kind === 'spreadsheet' ? 'sheets' : link.doc_kind === 'document' ? 'docs' : 'drive'}
          </a>
        </div>
        {/* Per-usage summary from PHASE 3.6 summarize_attachment fanout.
            Scoped to the current matter's issues in the payload; here
            we just render whatever the API sent. Same slot as
            attachment_summary on regular attachment rows. */}
        {link.attachment_summary && (
          <div
            className="mt-1 text-[11.5px] leading-snug me-text-dim"
            style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
          >
            {link.attachment_summary.length > SUMMARY_MAX_CHARS
              ? link.attachment_summary.slice(0, SUMMARY_MAX_CHARS) + '…'
              : link.attachment_summary}
          </div>
        )}
      </div>
    </div>
  )
}

// ─── Artifact row used by all three views ────────────────────────────────

// Cap on the rendered summary length. Today's MOVU max is 692 chars
// (avg 277). 1500 is comfortably above any current summary AND keeps a
// single pathological row from exploding the column. If we ever
// generate longer summaries, this bound + an ellipsis is the failure
// mode — a click into the artifact still shows the full text.
const SUMMARY_MAX_CHARS = 1500

// Exported so the Control-B preview windows can mount the REAL pane-2 card
// (Ken 2026-09-22) — a design preview must show the actual component, not a mock.
export function TimelineArtifactRow({
  artifact, matterId, selected, onSelect, showSpine, showChips, showDetails,
  // onOpenMarkup — opens the redline/comment modal (the markup pip is the
  // click target now). markupArtifactId dropped (no per-row open-state).
  onOpenMarkup, onOpenChangeCarry,
}: {
  artifact: Artifact
  /** Matter id powers per-artifact edits (e.g. ai_summary). */
  matterId?: number
  selected: boolean
  onSelect: () => void
  showSpine?: boolean
  showChips?: boolean
  /** When true, render the subject + sender/date/id metadata above the
   *  summary. When false (the default in matter views), the row is just
   *  the AI summary + badges. */
  showDetails?: boolean
  /** Pane-3 markup-mode signal: when this row's artifact id matches,
   *  the green "markup" badge is rendered in its "open" state (filled
   *  background) instead of the default outline. Only meaningful on
   *  attachment rows. */
  markupArtifactId?: number | null
  /** Callback fired when the green markup badge is clicked. Workspace
   *  promotes the artifact to selected AND turns on markup mode in
   *  one URL update so pane-3 swaps to the markup table. */
  onOpenMarkup?: (id: number) => void
  onOpenChangeCarry?: (data: ChangeCarry, filename: string) => void
  // Per Ken 2026-05-25: per-artifact `note` rendering removed (callout
  // 15). The DB field is still populated; just no UI surface.
}) {
  const { boldSubject } = useProductName()  // pane-2 subject/filename weight (QA)
  // Workspace = end-user surface, single-tenant → trimmed EMAL-000026 form
  // (Ken 2026-08-17, reversing the 08-10 bare-#number call). Full composite_id
  // is on hover via the title below.
  const code = shortId(artifact.composite_id) || `#${artifact.client_seq}`
  const fromName = artifact.from_name || artifact.from_email || ''
  const matterIsDemo = useContext(MatterDemoContext)  // CIC demo (proposed) matter
  // KEN "Least noisy" (Ken 2026-10-23, #3): drop the subject line only, keeping
  // the id/sender/date meta + summary. Context default false → MOVU unchanged.
  const { playhead: cardPlayhead, hide: hideSubject } = useContext(HideSubjectContext)
  // Documents are identified BY their subject (the filename) — never strip it,
  // even in "Least noisy". Only emails/attachments/chat lose the subject line.
  const hideSubjectLine = hideSubject && artifact.artifact_type !== 'document'
  // Is there anything above the summary in column 2? KEN grid (#1): only the
  // subject, and only when it's actually shown — governs the summary's top margin
  // so it hugs the top with no gap when the subject is hidden/absent.
  const subjectAbovePlayhead = !hideSubjectLine && !!artifact.subject
  const isUrgent = (artifact.urgency ?? 0) >= 4
  const Icon = artifact.artifact_type === 'email' ? Mail
            : artifact.artifact_type === 'attachment' ? Paperclip
            : artifact.artifact_type === 'chat' ? MessagesSquare
            : FileText
  // Pipeline v2 summary source per artifact_type:
  //   email/document → EmailIssue.summary (Summarize Email Issue step)
  //   attachment     → attachment_summary (Summarize Attachment step)
  // Falls back to v1 ai_summary on artifacts that haven't been re-run
  // through v2 (or have no filed issue in the current matter scope).
  const v2Summary = artifact.artifact_type === 'attachment'
    ? (artifact.attachment_summary || '')
    : (artifact.email_issue_summary || '')
  const summary = v2Summary || artifact.ai_summary || ''
  const isV2Summary = !!v2Summary
  const summaryDisplay = summary.length > SUMMARY_MAX_CHARS
    ? summary.slice(0, SUMMARY_MAX_CHARS) + '…'
    : summary
  // Multi-issue: this artifact is a fragment. `issueOrdinal` = this matter's
  // issue position (i of N); MultiIssueBlock names the sibling matters below.
  const issues = artifact.issues || []
  const issueCount = artifact.issue_count ?? issues.length
  const multiIssue = issueCount > 1
  const issueOrdinal = matterId != null
    ? issues.find((i) => i.matter_id === matterId)?.issue_index
    : undefined
  // id · sender · issue meta line. Shared by MOVU (where it sits above the
  // summary) and the KEN grid (where it's a footer beneath the summary, so
  // column 2's first line stays the subject/summary). The date is NOT in here on
  // the playhead — it lives in the grid's third column; MOVU never had a date
  // here either (its date is in the subject row's upper-right).
  const metaLine = (
    <div className="mt-0.5 flex items-center gap-1.5 text-[10.5px] me-text-mute">
      {artifact.artifact_type === 'attachment' && artifact.has_markup && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onOpenMarkup?.(artifact.id) }}
          title="View redlines + comments"
          className="-m-1.5 p-1.5 inline-flex items-center shrink-0"
        >
          <span
            aria-hidden
            style={{ width: 7, height: 7, borderRadius: '50%', background: '#BA7517', display: 'block' }}
          />
        </button>
      )}
      {/* Change-carry chip hidden on KEN/playhead (Ken T5 #1): change_carry is
          still computed + served, and still drives the chat corpus, but the
          pane-2 card shows no chip. The modal remains wired for other tenants. */}
      {!cardPlayhead && artifact.artifact_type === 'attachment' && artifact.change_carry && (() => {
        const cc = artifact.change_carry!
        const all = cc.carried === cc.total
        return (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onOpenChangeCarry?.(cc, artifact.subject || '') }}
            title="Changes carried from the version sent to the counterparty"
            className="-my-1 inline-flex shrink-0 items-center"
          >
            <span className="me-chip text-[10px]"
                  style={{ background: all ? 'var(--me-freshBg)' : 'var(--me-staleBg)',
                           color: all ? 'var(--me-fresh)' : 'var(--me-stale)' }}>
              {cc.carried} of {cc.total} carried
            </span>
          </button>
        )
      })()}
      <span className={`me-identifier ${cardPlayhead ? 'me-chrome-id tabular-nums' : 'me-mono'}`} title={artifact.composite_id || ''}>{code}</span>
      {fromName && artifact.artifact_type !== 'attachment' && <span className="me-identifier truncate">· {fromName}</span>}
      {artifact.parent_artifact && artifact.artifact_type !== 'attachment' && <Paperclip size={9} className="me-text-mute" />}
      {multiIssue && issueOrdinal != null && (
        <span className="me-text shrink-0">· issue {issueOrdinal} of {issueCount}</span>
      )}
    </div>
  )
  // Restructured from the original full-row <button>: use a div with a
  // small role="button" outer wrapper so EditableText (textarea) doesn't
  // sit inside a <button> and bubble keystrokes (spacebar) into a click.
  // Selection still happens on click anywhere outside the editable
  // children — we stop propagation INSIDE EditableText.
  return (
    <div
      onClick={() => { traceDestination({ type: 'card', id: artifact.id, label: artifact.subject ? `${code} · ${artifact.subject}` : code }); onSelect() }}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          // Mirror the implicit-button-keypress behavior, but only when
          // focus is on the row itself, not a nested input.
          if (e.target === e.currentTarget) {
            e.preventDefault()
            onSelect()
          }
        }
      }}
      data-selected={selected ? 'true' : undefined}
      data-artifact-id={artifact.id}
      className="me-row mb-0.5 w-full gap-2.5 text-left relative cursor-pointer"
      style={{
        padding: '8px 10px',
        ...(cardPlayhead
          // KEN grid (#1, 2026-10-23): one three-column grid — icon | content |
          // date — replacing the two-row flex. align-items:start keeps the icon
          // and date pinned to the first text line of the content column.
          ? { display: 'grid', gridTemplateColumns: 'auto 1fr auto', alignItems: 'start' as const }
          : { display: 'flex' }),
      }}
    >
      {showSpine && (
        <div
          className="absolute"
          style={{
            left: -19,
            top: 14,
            width: 10, height: 10,
            borderRadius: 5,
            background: 'var(--me-surface)',
            border: '1.5px solid var(--me-borderStrong, var(--me-border))',
          }}
        />
      )}
      {/* Markup pip — small #993C1D circle shown when an attachment has
          extracted redlines and/or comments (same condition that drove the
          old 'markup' button). Sits in the indent gutter to the LEFT of the
          paperclip, left-justified with the parent email's text column
          (the attachment row's own left edge), vertically centered on the
          paperclip. 2026-08-01. */}
      {/* Markup pip relocated next to the "markup" button below (same as the
          Google-Doc rows), instead of the absolute left-gutter dot. */}
      {/* Type icon (email/attachment) kept lighter than the summary text below
          (Ken 2026-08-11) — a soft grey, distinctly lighter than me-text-dim.
          When subj. lines are OFF the meta line below (its usual home) is
          hidden, so the markup pip is surfaced HERE — directly below the
          paperclip — so a marked-up document's modal stays reachable in the
          clean reading view (Ken 2026-08-11). */}
      <div className={`flex flex-col items-center shrink-0 ${cardPlayhead ? '' : 'mt-0.5'}`}>
        {/* Contract Desk intake transcript (Ken 2026-10-22) — a 'chat' artifact
            renders with a conversation icon, distinct from an email. Bypasses the
            media-backed Glyph so the chat icon always shows. */}
        {(() => {
          const iconNode = artifact.artifact_type === 'chat' ? (
            <MessagesSquare size={13} className="text-[#C4C1BA]" />
          ) : artifact.artifact_type === 'email' ? (
            // UXT-8 K2: stock lucide Mail envelope, 14px #a8a29e. Reverses the
            // GLY-0007 "summary slip" — no bespoke mark.
            <Mail size={14} className="text-[#a8a29e]" />
          ) : (
            // UXT-8 K2b: attachment paperclip, 14px #a8a29e, rotated so it reads
            // vertical rather than lucide's default diagonal. (doc slot media=null →
            // the lucide Icon=Paperclip child renders.)
            <Glyph name="doc" size={14}>
              <Icon size={14} className="text-[#a8a29e] -rotate-45" />
            </Glyph>
          )
          // KEN grid (#1, 2026-10-23): center the icon in a line-box matching the
          // first text line of column 2 so it sits on that line's cap height, not
          // the row's top edge (this also fixes the attachment half-line drift —
          // same bug, different icon metric). Off the playhead: bare icon, mt-0.5.
          return cardPlayhead
            ? <span className="flex items-center h-[18px]">{iconNode}</span>
            : iconNode
        })()}
        {!showDetails && artifact.artifact_type === 'attachment' && artifact.has_markup && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onOpenMarkup?.(artifact.id) }}
            title="View redlines + comments"
            className="mt-1 p-1 inline-flex items-center justify-center"
          >
            <span
              aria-hidden
              style={{ width: 7, height: 7, borderRadius: '50%', background: '#BA7517', display: 'block' }}
            />
          </button>
        )}
      </div>
      <div className="flex-1 min-w-0">
        {/* Subject + bibliographic metadata only when `details` is on.
         *  When details is off the row is just the AI summary (clean
         *  reading view); the subject lives in the third-column
         *  artifact viewer where it's always editable. */}
        {showDetails && (
          <>
            {/* Subject line = column-2 first line. KEN grid (#1, 2026-10-23): the
                subject NODE is conditionally rendered — when hidden (or absent on
                the playhead) it is simply not emitted, so no empty row is left and
                column 2's first line becomes the summary. The date lives in the
                grid's third column on the playhead, NOT here. Off the playhead this
                renders exactly as before (date in the upper-right, "(no subject)"
                placeholder kept). */}
            {!hideSubjectLine && (artifact.subject || !cardPlayhead) && (
              <div className="flex items-baseline gap-2 min-w-0">
                <span className={`me-record me-subject text-[12px] me-text flex-1 min-w-0 ${boldSubject ? 'font-semibold' : 'font-normal'}`}>
                  {/* Read-only subject / filename — inline editing removed
                      2026-08-01 (Emerson: no upside to letting users rename
                      emails or document filenames in the chronology). */}
                  {artifact.subject || <span className="me-text-mute">(no subject)</span>}
                </span>
                {/* MOVU only: date in the subject row's upper-right. On the KEN grid
                    the date moves to column 3 (attachments never show it either). */}
                {!cardPlayhead && artifact.artifact_type !== 'attachment' && artifact.source_date && (
                  <span className="me-mono text-[10.5px] me-text-mute shrink-0">
                    {fmtMonthDay(artifact.source_date)}
                  </span>
                )}
              </div>
            )}
            {/* id · sender meta — MOVU keeps it here (above the summary). The KEN
                grid renders it as a footer BELOW the summary (see after the summary
                block) so column 2's first line stays the subject/summary. */}
            {!cardPlayhead && metaLine}
          </>
        )}
        {/* Per Ken 2026-05-25: per-artifact `note` rendering removed
            (callout 15) — the italicized one-liner under each artifact
            row is gone. Note data still lives in the DB so we don't
            lose history; just not displayed. */}
        {/* CIC demo: a "proposed" word on the parent email card — the matter is
            about to be instantiated (Ken 2026-10-01). Same purple as the folder/
            matter pips. */}
        {matterIsDemo && artifact.artifact_type === 'email' && (
          <div className="me-mono uppercase tracking-wide"
               style={{ fontSize: 10, color: '#8b5cf6', fontWeight: 700 }}>proposed</div>
        )}
        {/* AI summary — the primary content of the row. Editable inline
            so users can correct AI summaries without opening the artifact
            viewer. (Editor click bubbles are stopped by EditableText.) */}
        <div
          className={`me-chrono leading-snug me-text-dim ${(cardPlayhead ? subjectAbovePlayhead : showDetails) ? 'mt-1' : ''}`}
          style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
        >
          {/* Cleanest view (showDetails OFF): the date floats to the upper-right
              and the summary starts at the top-left and wraps under it — no
              stacked line, no gap where the shortId used to be (Ken 2026-08-18).
              Attachments have no inherited date → no float. */}
          {!showDetails && artifact.artifact_type !== 'attachment' && artifact.source_date && (
            <span
              className="me-mono text-[10.5px] me-text-mute"
              style={{ float: 'right', marginLeft: '0.5rem' }}
            >
              {fmtMonthDay(artifact.source_date)}
            </span>
          )}
          {isV2Summary ? (
            // v2 summary lives on EmailIssue.summary — read-only here so
            // an inline edit doesn't silently land on the wrong field
            // (ai_summary). Edit support for EmailIssue.summary is a
            // separate change.
            <span>{summaryDisplay}</span>
          ) : (
          <EditableText
            value={summaryDisplay}
            target={{ type: 'artifact', id: artifact.id, field: 'ai_summary' }}
            mode="block"
            multiline
            maxLength={4000}
            invalidateKeys={[
              ['artifact', artifact.id],
              // The matter list query backs the timeline view's artifact data;
              // refresh both possible cache buckets after an edit.
              ['artifacts', 'matter', matterId ?? -1],
              ['artifactsPage'],
            ]}
            placeholder={
              // Demo (proposed) matters AND the demo/playhead tenant: never show
              // the dev-y "run pipeline" prompt — it's a sandbox, not a real
              // pipeline target (Ken 2026-10-01; T3 #5 2026-11-02). Show the
              // filename for docs/attachments, otherwise nothing.
              matterIsDemo || cardPlayhead
                ? (artifact.artifact_type === 'attachment' || artifact.artifact_type === 'document'
                    ? (showDetails ? '' : (artifact.subject || ''))
                    : '')
              // Documents (e.g. attached from Drive) aren't pipelined, so
              // "run pipeline to generate" is misleading. Show the filename
              // instead (Ken 2026-08-18) — but only in cleanest view, since
              // details view already shows the filename in the header above.
              : artifact.artifact_type === 'document'
                ? (showDetails ? '' : (artifact.subject || '(document)'))
                : '(no AI summary — run pipeline to generate)'
            }
          />
          )}
        </div>
        {/* KEN grid (#1): id · sender meta as a footer beneath the summary, so the
            summary (or subject) stays column 2's first line. MOVU renders it above
            the summary instead (see the subject block). */}
        {showDetails && cardPlayhead && metaLine}
        {multiIssue && (
          <MultiIssueBlock
            issues={issues}
            mode="scoped"
            currentMatterId={matterId}
            collapsed={!showDetails}
          />
        )}
        {(showChips || isUrgent || artifact.is_sensitive) && (
          <div className="mt-1.5 flex flex-wrap gap-1">
            {isUrgent && (
              <span className="me-chip me-chip-urgent inline-flex items-center gap-1 text-[10px]">
                <Zap size={9} /> urgent
              </span>
            )}
            {artifact.is_sensitive && (
              <span className="me-chip me-chip-stale inline-flex items-center gap-1 text-[10px]">
                <AlertTriangle size={9} /> sensitive
              </span>
            )}
          </div>
        )}
      </div>
      {/* Column 3 — the date, aligned (via the grid's align-items:start + a matched
          line-box) to the first text line of column 2. Attachments never show the
          inherited parent date, so their cell is empty (0-width auto column). KEN
          grid only; MOVU uses the two-column flex layout and keeps its date in the
          subject row above. Gated on showDetails so it doesn't double the cleanest-
          view float-date (which the summary block renders when details are off). */}
      {cardPlayhead && showDetails && (
        <div className="shrink-0">
          {artifact.artifact_type !== 'attachment' && artifact.source_date && (
            <span className="flex items-center h-[18px] me-mono text-[10.5px] me-text-mute">
              {fmtMonthDay(artifact.source_date)}
            </span>
          )}
        </div>
      )}
    </div>
  )
}

// ─── Helpers ──────────────────────────────────────────────────────────────

/** Ken 2026-07-08 tweak #5 — cards in the chronology use a simple M/DD
 *  or MM/DD (no year) — natural month (unpadded), day zero-padded to
 *  two digits. So January 5 renders as "1/05", October 5 as "10/05". */
function fmtMonthDay(iso: string): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (isNaN(d.getTime())) return ''
  const m = (d.getMonth() + 1).toString()
  const dd = d.getDate().toString().padStart(2, '0')
  return `${m}/${dd}`
}

function fmtDateShort(iso: string): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (isNaN(d.getTime())) return ''
  const m = d.toLocaleString('en-US', { month: 'short' })
  return `${m} ${d.getDate()}`
}
